// EXPORTS: IExercise, MOCK_EXERCISES, BodyPart, EquipmentType, SubCategory, SUBCATEGORY_LABELS, getExercisesByBodyPart, getExerciseById, MUSCLE_GROUPS, getMuscleGroupOfExercise, getMuscleGroupCount, getMuscleGroupLabel, getExercisesByMuscleGroup, MuscleGroup, DifficultyLevel

export type BodyPart =
  | 'chest' | 'back' | 'shoulders' | 'legs' | 'arms' | 'abs'
  | 'warmup' | 'stretch' | 'calves';

export type MuscleGroup = 'chest' | 'back' | 'shoulders' | 'arms' | 'legs' | 'abs' | 'warmup' | 'stretch';

export interface IMuscleGroupMeta {
  key: MuscleGroup;
  label: string;
  bodyParts: BodyPart[];
}

/** 8 大部位模块（胸/背/肩/手臂/腿/核心/激活/放松拉伸） */
export const MUSCLE_GROUPS: IMuscleGroupMeta[] = [
  { key: 'chest', label: '胸部', bodyParts: ['chest'] },
  { key: 'back', label: '背部', bodyParts: ['back'] },
  { key: 'shoulders', label: '肩部', bodyParts: ['shoulders'] },
  { key: 'arms', label: '手臂', bodyParts: ['arms'] },
  { key: 'legs', label: '腿部', bodyParts: ['legs', 'calves'] },
  { key: 'abs', label: '腹部核心', bodyParts: ['abs'] },
  { key: 'warmup', label: '激活', bodyParts: ['warmup'] },
  { key: 'stretch', label: '放松拉伸', bodyParts: ['stretch'] },
];

/** 判定是否为激活/热身类动作（按名称关键词） */
function isActivationExercise(ex: IExercise): boolean {
  if (ex.isWarmup) return true;
  if (ex.bodyPart === 'warmup') return true;
  const name = ex.name;
  // 明确的力量训练关键词（命中则肯定不是激活）
  const strengthKeywords = [
    '卧推', '划船', '弯举', '深蹲', '硬拉', '推举', '飞鸟', '侧平举', '前平举',
    '臂屈伸', '下拉', '夹胸', '保加利亚', '推肩', '实力推', '仰卧臂屈伸',
    '髋内收', '内收肌', '仰卧髋', '侧卧髋',
  ];
  for (const kw of strengthKeywords) {
    if (name.includes(kw)) return false;
  }
  // 名称关键词判定（仅热身/激活类）
  const activationKeywords = [
    '激活', '热身', '准备', '启动', '预激活', '肩袖', '手腕', '脚踝',
    '绕肩', '侧向移动弹力带', '蜘蛛式热身', '下斜方激活',
    '肩胛骨激活', '胸椎屈曲激活', '胸椎本体感受激活', 'Trx激活',
    '外旋肌激活', '内旋肌激活', '手肘搭平板凳', '头枕地对抗',
    '臀中肌臀小肌激活', '动态青蛙趴', '髋关节90/90', '屈髋抬腿',
    '靠墙单臂肩胛前引', '弹力带胸部激活', '弹力带肩部热身', '弹力带绕肩',
    '训练前准备', '肩袖激活',
  ];
  for (const kw of activationKeywords) {
    if (name.includes(kw)) return true;
  }
  // 泡沫轴松解类归入放松拉伸，不归激活
  if (name.includes('泡沫轴') || name.includes('松解') || name.includes('放松')) return false;
  // Y字系列：仅「俯卧Y字举」「下斜方激活Y字」是激活；「Y字侧平举」是力量训练（已被侧平举排除）
  if (name === '俯卧Y字举' || name.includes('下斜方激活') || name.includes('下斜方Y字')) return true;
  return false;
}

/** 判定是否为放松/拉伸类动作（按名称关键词） */
function isRelaxationExercise(ex: IExercise): boolean {
  if (ex.isStretch) return true;
  if (ex.bodyPart === 'stretch') return true;
  const relaxKeywords = [
    '拉伸', '放松', '松解', '泡沫轴', '筋膜', '侧弓步动态', '世界最伟大',
    '肩胛提肌', '胸锁乳突肌', '斜角肌', '颈阔肌', '拉伸对抗',
  ];
  const name = ex.name;
  for (const kw of relaxKeywords) {
    if (name.includes(kw)) return true;
  }
  // subCategory 是 chest-stretch / back-stretch 等
  if (ex.subCategory?.includes('stretch')) return true;
  return false;
}

/** 二次分类：bodyPart → 8大部位模块
 *  优先按动作性质（激活/放松拉伸）归位，再按 bodyPart 归到 6 大训练部位 */
export function getMuscleGroupOfExercise(ex: IExercise): MuscleGroup {
  // 优先判定：放松/拉伸类 → 放松拉伸库
  if (isRelaxationExercise(ex)) return 'stretch';
  // 优先判定：激活/热身类 → 激活库
  if (isActivationExercise(ex)) return 'warmup';
  // 按 bodyPart 归位到 6 大训练部位
  const bp = ex.bodyPart;
  if (bp === 'chest') return 'chest';
  if (bp === 'back') return 'back';
  if (bp === 'shoulders') return 'shoulders';
  if (bp === 'arms') return 'arms';
  if (bp === 'legs' || bp === 'calves') return 'legs';
  if (bp === 'abs') return 'abs';
  return 'abs';
}

export function getMuscleGroupCount(groupKey: MuscleGroup, allExercises: IExercise[]): number {
  let count = 0;
  for (const ex of allExercises) {
    if (getMuscleGroupOfExercise(ex) === groupKey) count++;
  }
  return count;
}

export function getExercisesByMuscleGroup(groupKey: MuscleGroup, allExercises: IExercise[]): IExercise[] {
  return allExercises.filter(ex => getMuscleGroupOfExercise(ex) === groupKey);
}

export function getMuscleGroupLabel(groupKey: MuscleGroup): string {
  return MUSCLE_GROUPS.find(g => g.key === groupKey)?.label ?? '';
}

export type DifficultyLevel = 'beginner' | 'intermediate' | 'advanced';

export type EquipmentType =
  | 'barbell' | 'dumbbell' | 'machine' | 'bodyweight'
  | 'cable' | 'kettlebell' | 'foam-roller' | 'resistance-band'
  | 'trx' | 'ab-wheel';

// 细分分类 key（每个大部位下有若干细分）
export type SubCategory = string;

 // 全局 subCategory 兜底映射（跨 bodyPart 共享的英文细分名）
export const GLOBAL_SUBCATEGORY_LABELS: Record<string, string> = {
  // 胸
  'upper-chest': '上胸',
  'mid-chest': '中胸',
  'lower-chest': '下胸',
  'inner-chest': '中缝',
  // 背
  'width': '宽度',
  'thickness': '厚度',
  'lat': '背阔肌',
  'lats': '背阔肌',
  'row': '划船类',
  'rear-delt': '后束',
  'rear-deltoid': '后束',
  'mid-back': '中背',
  // 肩
  'front-deltoid': '前束',
  'side-deltoid': '中束',
  'medial-deltoid': '中束',
  'medial-delt': '中束',
  'anterior-delt': '前束',
  // 腿
  'quadriceps': '股四头肌',
  'quads': '股四头肌',
  'hamstrings': '腘绳肌',
  'glutes': '臀部',
  'calves': '小腿',
  'hamstrings-glutes': '腘绳+臀',
  'quads-glutes': '股四+臀',
  'lower-back': '下背',
  'hip-flexor': '髋屈肌',
  'inner-thigh': '大腿内侧',
  // 手臂
  'biceps': '二头肌',
  'triceps': '三头肌',
  // 腹
  'upper-abs': '上腹',
  'lower-abs': '下腹',
  'obliques': '侧腹',
  'core': '核心',
  'posture': '体态改善',
  // 小腿
  'gastrocnemius': '腓肠肌',
  'soleus': '比目鱼肌',
  // 热身
  'full-body': '全身激活',
  'shoulder': '肩部激活',
  'hip-leg': '髋腿激活',
  'thoracic': '胸椎/核心激活',
  'foam-roller': '泡沫轴放松',
  'chest': '胸部激活',
  'general': '综合热身',
  'abs': '腹部激活',
  'arms': '手臂激活',
  'back': '背部激活',
  'legs': '腿部激活',
  'neck': '颈部激活',
  // 拉伸
  'chest-stretch': '胸部拉伸',
  'back-stretch': '背部拉伸',
  'shoulder-stretch': '肩部拉伸',
  'arm-stretch': '手臂拉伸',
  'leg-stretch': '腿部拉伸',
  'abs-stretch': '腹部拉伸',
  'full-stretch': '全身拉伸',
};

/** 根据 bodyPart + subCategory 获取中文标签，优先部位内映射，兜底用全局映射 */
export function getSubCategoryLabel(bodyPart: BodyPart, subCategory: string): string {
  const partMap = SUBCATEGORY_LABELS[bodyPart];
  if (partMap && partMap[subCategory]) return partMap[subCategory];
  if (GLOBAL_SUBCATEGORY_LABELS[subCategory]) return GLOBAL_SUBCATEGORY_LABELS[subCategory];
  // 已经是中文则直接返回
  if (/[\u4e00-\u9fa5]/.test(subCategory)) return subCategory;
  return subCategory;
}
export const SUBCATEGORY_LABELS: Record<BodyPart, Record<SubCategory, string>> = {
  chest: {
    'upper-chest': '上胸',
    'mid-chest': '中胸',
    'lower-chest': '下胸',
    'inner-chest': '中缝',
  },
   back: {
     'width': '宽度',
     'thickness': '厚度',
     'lat': '背阔肌',
     'row': '划船类',
     'rear-delt': '后束（背部）',
     'lats': '背阔肌',
     'mid-back': '中背',
   },
   shoulders: {
     'front-deltoid': '前束',
     'side-deltoid': '中束',
     'rear-deltoid': '后束',
     'medial-deltoid': '中束',
     'medial-delt': '中束',
     'anterior-delt': '前束',
   },
   legs: {
     'quadriceps': '股四头',
     'hamstrings': '腘绳肌',
     'glutes': '臀部',
     'calves': '小腿',
     'quads': '股四头',
     'hamstrings-glutes': '腘绳+臀',
     'quads-glutes': '股四+臀',
     'lower-back': '下背',
     'hip-flexor': '髋屈肌',
     'inner-thigh': '大腿内侧',
   },
  arms: {
    'biceps': '二头肌',
    'triceps': '三头肌',
  },
   abs: {
     'upper-abs': '上腹',
     'lower-abs': '下腹',
     'obliques': '侧腹（核心）',
     'posture': '体态改善',
     'core': '核心',
   },
   warmup: {
     'full-body': '全身激活',
     'shoulder': '肩部激活',
     'hip-leg': '髋腿激活',
     'thoracic': '胸椎/核心激活',
     'foam-roller': '泡沫轴放松',
     'chest': '胸部激活',
     'core': '核心激活',
     'general': '综合热身',
     '放松': '放松',
     '激活': '激活',
     '拉伸': '拉伸',
     '髋': '髋部激活',
     '松解': '泡沫轴松解',
   },
   stretch: {
     'chest-stretch': '胸部拉伸',
     'back-stretch': '背部拉伸',
     'shoulder-stretch': '肩部拉伸',
     'arm-stretch': '手臂拉伸',
     'leg-stretch': '腿部拉伸',
     'abs-stretch': '腹部拉伸',
     'shoulder': '肩部拉伸',
     'neck': '颈部拉伸',
     'full-stretch': '全身拉伸',
   },
  calves: {
    gastrocnemius: '腓肠肌',
    soleus: '比目鱼肌',
  },
};

export const DIFFICULTY_LABELS: Record<DifficultyLevel, string> = {
  beginner: '入门',
  intermediate: '中级',
  advanced: '高级',
};

export interface IExercise {
  id: string;
  name: string;
  bodyPart: BodyPart;
  equipment: EquipmentType;
  /** 细分分类（动作库按细分分组展示用） */
  subCategory: SubCategory;
  tips: string[];
  sets: number;
  reps: string;
  rpe: number;
  restSeconds: number;
  isBodyweight: boolean;
  isStretch?: boolean;
  isWarmup?: boolean;
  /** 难度等级：入门 / 中级 / 高级 */
  difficulty?: DifficultyLevel;
  /** 主要目标肌群（中文标签数组） */
  targetMuscles?: string[];
  premium?: boolean;
  /** 精讲要点（仅部分动作有，用于动作详情页展示） */
  detailGuide?: string[];
  /** 动作来源版本号，有则在详情页标注 */
  source?: string;
  /** 示范图状态：'ready' 已就绪 / 'pending' 待更新，缺省时视为 ready */
  demoStatus?: 'ready' | 'pending';
  /** 动作演示视频地址（瘦身 MP4） */
  demoUrl?: string;
  /** 完整教学视频地址（长视频，预留；有值时显示播放角标） */
  videoUrl?: string;
}

  // ========== 动作库（四分化训练合集 · 32 个动作） ==========
  export const MOCK_EXERCISES: IExercise[] = [
    // ========== 第1期（推：胸/肩/三头） ==========
    {
      id: 'band-shoulder-warmup',
      name: '弹力带肩部热身',
      bodyPart: 'warmup', equipment: 'resistance-band', subCategory: 'shoulder',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 2, reps: '15', rpe: 4, restSeconds: 30, isBodyweight: false, isWarmup: true, difficulty: 'beginner', targetMuscles: ['全身激活肌群'],
      demoUrl: '/media/gifs/band-shoulder-warmup',
      videoUrl: '/media/videos/band-shoulder-warmup',
    },
    {
      id: 'barbell-flat-press',
      name: '杠铃平板卧推',
      bodyPart: 'chest', equipment: 'barbell', subCategory: 'mid-chest',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 4, reps: '8-12', rpe: 8, restSeconds: 90, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['胸大肌', '三角肌前束'],
      demoUrl: '/media/gifs/barbell-flat-press',
      videoUrl: '/media/videos/barbell-flat-press',
    },
    {
      id: 'cable-crossover',
      name: '龙门架绳索夹胸',
      bodyPart: 'chest', equipment: 'cable', subCategory: 'inner-chest',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['胸大肌中缝', '前锯肌'],
      demoUrl: '/media/gifs/cable-crossover',
      videoUrl: '/media/videos/cable-crossover',
    },
  // ========== 第2期（拉：背/二头） ==========
    {
      id: 'incline-bench-row',
      name: '上斜凳坐姿划船',
      bodyPart: 'back', equipment: 'machine', subCategory: 'thickness',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 4, reps: '8-12', rpe: 8, restSeconds: 75, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['斜方肌中下部', '菱形肌', '背阔肌'],
      demoUrl: '/media/gifs/incline-bench-row',
      videoUrl: '/media/videos/incline-bench-row',
    },
    {
      id: 'barbell-bicep-curl',
      name: '站姿杠铃弯举',
      bodyPart: 'arms', equipment: 'barbell', subCategory: 'biceps',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 3, reps: '10-12', rpe: 8, restSeconds: 60, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['肱二头肌', '肱肌'],
      demoUrl: '/media/gifs/barbell-bicep-curl',
      videoUrl: '/media/videos/barbell-bicep-curl',
    },
    {
      id: 'kneel-tricep-dip',
      name: '跪姿肘屈伸',
      bodyPart: 'arms', equipment: 'bodyweight', subCategory: 'triceps',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 2, reps: '10', rpe: 4, restSeconds: 30, isBodyweight: true, isWarmup: true, difficulty: 'beginner', targetMuscles: ['肱三头肌', '肘肌'],
      demoUrl: '/media/gifs/kneel-tricep-dip',
      videoUrl: '/media/videos/kneel-tricep-dip',
    },
    {
      id: 'wall-scapular-protraction',
      name: '靠墙单臂肩胛前引',
      bodyPart: 'back', equipment: 'bodyweight', subCategory: 'width',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 2, reps: '每侧10', rpe: 4, restSeconds: 30, isBodyweight: true, isWarmup: true, difficulty: 'beginner', targetMuscles: ['背阔肌', '大圆肌'],
      demoUrl: '/media/gifs/wall-scapular-protraction',
      videoUrl: '/media/videos/wall-scapular-protraction',
    },
  // ========== 第3期（腿） ==========
    {
      id: 'hip-adductor-machine',
      name: '仰卧髋内收器械',
      bodyPart: 'legs', equipment: 'machine', subCategory: 'glutes',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['臀大肌', '腘绳肌'],
      demoUrl: '/media/gifs/hip-adductor-machine',
      videoUrl: '/media/videos/hip-adductor-machine',
    },
    {
      id: 'side-lying-hip-adduct',
      name: '侧卧髋内收',
      bodyPart: 'legs', equipment: 'bodyweight', subCategory: 'glutes',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 3, reps: '每侧12', rpe: 8, restSeconds: 60, isBodyweight: true, difficulty: 'advanced', targetMuscles: ['臀大肌', '腘绳肌'],
      demoUrl: '/media/gifs/side-lying-hip-adduct',
      videoUrl: '/media/videos/side-lying-hip-adduct',
    },
    {
      id: 'side-lunge-stretch',
      name: '侧弓步动态拉伸',
      bodyPart: 'stretch', equipment: 'bodyweight', subCategory: 'leg-stretch',
      tips: ['核心收紧，控制动作节奏', '全程保持稳定'],
      sets: 2, reps: '每侧10', rpe: 3, restSeconds: 20, isBodyweight: true, isStretch: true, isWarmup: true, difficulty: 'beginner', targetMuscles: ['目标拉伸肌群'],
      demoUrl: '/media/gifs/side-lunge-stretch',
      videoUrl: '/media/videos/side-lunge-stretch',
    },
    {
      id: 'bulgarian-split-squat',
      name: '保加利亚分腿蹲',
      bodyPart: 'legs', equipment: 'dumbbell', subCategory: 'glutes',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 3, reps: '每侧8-10', rpe: 8, restSeconds: 75, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['臀大肌', '腘绳肌'],
      demoUrl: '/media/gifs/bulgarian-split-squat',
      videoUrl: '/media/videos/bulgarian-split-squat',
    },
    {
      id: 'single-leg-dumbbell-dl',
      name: '单腿哑铃硬拉',
      bodyPart: 'legs', equipment: 'dumbbell', subCategory: 'hamstrings',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 3, reps: '每侧10', rpe: 8, restSeconds: 75, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['腘绳肌', '臀大肌'],
      demoUrl: '/media/gifs/single-leg-dumbbell-dl',
      videoUrl: '/media/videos/single-leg-dumbbell-dl',
    },
    {
      id: 'dumbbell-lunge',
      name: '哑铃弓步蹲',
      bodyPart: 'legs', equipment: 'dumbbell', subCategory: 'quadriceps',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 3, reps: '每侧10', rpe: 8, restSeconds: 75, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['股四头肌', '臀大肌'],
      demoUrl: '/media/gifs/dumbbell-lunge',
      videoUrl: '/media/videos/dumbbell-lunge',
    },
    {
      id: 'standing-calf-raise',
      name: '站姿提踵',
      bodyPart: 'legs', equipment: 'bodyweight', subCategory: 'calves',
      tips: ['核心收紧，控制动作节奏', '全程保持稳定'],
      sets: 4, reps: '15-20', rpe: 8, restSeconds: 45, isBodyweight: true, difficulty: 'advanced', targetMuscles: ['腓肠肌', '比目鱼肌'],
      demoUrl: '/media/gifs/standing-calf-raise',
      videoUrl: '/media/videos/standing-calf-raise',
    },
  // ========== 第4期（上肢混合/肩） ==========
    {
      id: 'supine-cable-tricep',
      name: '仰卧绳索臂屈伸',
      bodyPart: 'arms', equipment: 'cable', subCategory: 'triceps',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['肱三头肌', '肘肌'],
      demoUrl: '/media/gifs/supine-cable-tricep',
      videoUrl: '/media/videos/supine-cable-tricep',
    },
    {
      id: 'prone-dumbbell-fly',
      name: '俯卧哑铃飞鸟',
      bodyPart: 'shoulders', equipment: 'dumbbell', subCategory: 'rear-deltoid',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['三角肌后束', '斜方肌下部'],
      demoUrl: '/media/gifs/prone-dumbbell-fly',
      videoUrl: '/media/videos/prone-dumbbell-fly',
    },
    {
      id: 'push-up',
      name: '俯卧撑',
      bodyPart: 'chest', equipment: 'bodyweight', subCategory: 'mid-chest',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 3, reps: '力竭', rpe: 9, restSeconds: 60, isBodyweight: true, difficulty: 'advanced', targetMuscles: ['胸大肌', '三角肌前束'],
      demoUrl: '/media/gifs/push-up',
      videoUrl: '/media/videos/push-up',
    },
    {
      id: 'single-leg-dl-glute',
      name: '单腿哑铃硬拉',
      bodyPart: 'legs', equipment: 'dumbbell', subCategory: 'glutes',
      tips: ['Day4 热身用：低强度激活腘绳+臀部', '单腿支撑，髋部水平后推，控制节奏'],
      sets: 2, reps: '每侧10', rpe: 4, restSeconds: 30, isBodyweight: false, isWarmup: true, difficulty: 'beginner', targetMuscles: ['臀大肌', '腘绳肌'],
      demoUrl: '/media/gifs/single-leg-dl-glute',
      videoUrl: '/media/videos/single-leg-dl-glute',
    },
    {
      id: 'seated-cable-curl',
      name: '坐姿绳索二头弯举',
      bodyPart: 'arms', equipment: 'cable', subCategory: 'biceps',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['肱二头肌', '肱肌'],
      demoUrl: '/media/gifs/seated-cable-curl',
      videoUrl: '/media/videos/seated-cable-curl',
    },
    {
      id: 'band-shoulder-pull',
      name: '弹力带肩部前后拉',
      bodyPart: 'shoulders', equipment: 'resistance-band', subCategory: 'shoulder',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 2, reps: '15', rpe: 4, restSeconds: 30, isBodyweight: false, isWarmup: true, difficulty: 'beginner', targetMuscles: ['全身激活肌群'],
      demoUrl: '/media/gifs/band-shoulder-pull',
      videoUrl: '/media/videos/band-shoulder-pull',
    },
    {
      id: 'cable-face-pull',
      name: '绳索面拉',
      bodyPart: 'shoulders', equipment: 'cable', subCategory: 'rear-deltoid',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['三角肌后束', '斜方肌下部'],
      demoUrl: '/media/gifs/cable-face-pull',
      videoUrl: '/media/videos/cable-face-pull',
    },
    {
      id: 'behind-neck-cable-tricep',
      name: '颈后绳索臂屈伸',
      bodyPart: 'arms', equipment: 'cable', subCategory: 'triceps',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['肱三头肌', '肘肌'],
      demoUrl: '/media/gifs/behind-neck-cable-tricep',
      videoUrl: '/media/videos/behind-neck-cable-tricep',
    },

  // ========== 居家训练合集 · 29 个动作（独立 ID，不与四分化共用） ==========
    // --- 第1期（推：胸/肩/三头） ---
    {
      id: 'home-prone-reverse-fly',
      name: '俯卧反向飞鸟',
      bodyPart: 'shoulders', equipment: 'dumbbell', subCategory: 'rear-deltoid',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 2, reps: '15', rpe: 4, restSeconds: 30, isBodyweight: false, isWarmup: true, difficulty: 'beginner', targetMuscles: ['三角肌后束', '斜方肌下部'],
      demoUrl: '/media/gifs/home-prone-reverse-fly',
      videoUrl: '/media/videos/home-prone-reverse-fly',
    },
    {
      id: 'home-bent-tricep-kickback',
      name: '俯身哑铃臂屈伸',
      bodyPart: 'arms', equipment: 'dumbbell', subCategory: 'triceps',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['肱三头肌', '肘肌'],
      demoUrl: '/media/gifs/home-bent-tricep-kickback',
      videoUrl: '/media/videos/home-bent-tricep-kickback',
    },
    {
      id: 'home-dumbbell-pushup',
      name: '哑铃俯卧撑',
      bodyPart: 'chest', equipment: 'dumbbell', subCategory: 'mid-chest',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 4, reps: '力竭', rpe: 9, restSeconds: 75, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['胸大肌', '三角肌前束'],
      demoUrl: '/media/gifs/home-dumbbell-pushup',
      videoUrl: '/media/videos/home-dumbbell-pushup',
    },
    {
      id: 'home-dumbbell-flat-press',
      name: '哑铃平板卧推',
      bodyPart: 'chest', equipment: 'dumbbell', subCategory: 'mid-chest',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 4, reps: '8-12', rpe: 8, restSeconds: 75, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['胸大肌', '三角肌前束'],
      demoUrl: '/media/gifs/home-dumbbell-flat-press',
      videoUrl: '/media/videos/home-dumbbell-flat-press',
    },
    {
      id: 'home-dumbbell-fly',
      name: '哑铃飞鸟',
      bodyPart: 'chest', equipment: 'dumbbell', subCategory: 'inner-chest',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['胸大肌中缝', '前锯肌'],
      demoUrl: '/media/gifs/home-dumbbell-fly',
      videoUrl: '/media/videos/home-dumbbell-fly',
    },
    {
      id: 'home-seated-behind-neck-tricep',
      name: '坐姿颈后臂屈伸',
      bodyPart: 'arms', equipment: 'dumbbell', subCategory: 'triceps',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['肱三头肌', '肘肌'],
      demoUrl: '/media/gifs/home-seated-behind-neck-tricep',
      videoUrl: '/media/videos/home-seated-behind-neck-tricep',
    },
    {
      id: 'home-band-chest-activate',
      name: '弹力带胸部激活',
      bodyPart: 'chest', equipment: 'resistance-band', subCategory: 'chest',
      tips: ['核心收紧，控制动作节奏', '全程保持稳定'],
      sets: 2, reps: '15', rpe: 4, restSeconds: 30, isBodyweight: false, isWarmup: true, difficulty: 'beginner', targetMuscles: ['全身激活肌群'],
      demoUrl: '/media/gifs/home-band-chest-activate',
      videoUrl: '/media/videos/home-band-chest-activate',
    },
    {
      id: 'home-wall-handstand',
      name: '靠墙倒立',
      bodyPart: 'shoulders', equipment: 'bodyweight', subCategory: 'front-deltoid',
      tips: ['核心收紧，控制动作节奏', '全程保持稳定'],
      sets: 3, reps: '30秒', rpe: 8, restSeconds: 60, isBodyweight: true, difficulty: 'advanced', targetMuscles: ['三角肌前束', '三角肌中束'],
      demoUrl: '/media/gifs/home-wall-handstand',
      videoUrl: '/media/videos/home-wall-handstand',
    },
    // --- 第2期（拉：背/二头/核心） ---
    {
      id: 'home-prone-back-extension',
      name: '俯卧背伸',
      bodyPart: 'back', equipment: 'bodyweight', subCategory: 'width',
      tips: ['核心收紧，控制动作节奏', '全程保持稳定'],
      sets: 2, reps: '15', rpe: 4, restSeconds: 30, isBodyweight: true, isWarmup: true, difficulty: 'beginner', targetMuscles: ['背阔肌', '大圆肌'],
      demoUrl: '/media/gifs/home-prone-back-extension',
      videoUrl: '/media/videos/home-prone-back-extension',
    },
    {
      id: 'home-single-arm-dumbbell-row',
      name: '单臂哑铃划船',
      bodyPart: 'back', equipment: 'dumbbell', subCategory: 'thickness',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 4, reps: '每侧10-12', rpe: 8, restSeconds: 75, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['斜方肌中下部', '菱形肌', '背阔肌'],
      demoUrl: '/media/gifs/home-single-arm-dumbbell-row',
      videoUrl: '/media/videos/home-single-arm-dumbbell-row',
    },
    {
      id: 'home-double-dumbbell-bent-row',
      name: '双哑铃俯身划船',
      bodyPart: 'back', equipment: 'dumbbell', subCategory: 'thickness',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 4, reps: '8-12', rpe: 8, restSeconds: 75, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['斜方肌中下部', '菱形肌', '背阔肌'],
      demoUrl: '/media/gifs/home-double-dumbbell-bent-row',
      videoUrl: '/media/videos/home-double-dumbbell-bent-row',
    },
    {
      id: 'home-dumbbell-curl',
      name: '哑铃弯举',
      bodyPart: 'arms', equipment: 'dumbbell', subCategory: 'biceps',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['肱二头肌', '肱肌'],
      demoUrl: '/media/gifs/home-dumbbell-curl',
      videoUrl: '/media/videos/home-dumbbell-curl',
    },
    {
      id: 'home-pull-up',
      name: '引体向上',
      bodyPart: 'back', equipment: 'bodyweight', subCategory: 'width',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 4, reps: '力竭', rpe: 9, restSeconds: 75, isBodyweight: true, difficulty: 'advanced', targetMuscles: ['背阔肌', '大圆肌'],
      demoUrl: '/media/gifs/home-pull-up',
      videoUrl: '/media/videos/home-pull-up',
    },
    // --- 第3期（腿/臀/核心） ---
    {
      id: 'home-worlds-greatest-stretch',
      name: '世界最伟大拉伸',
      bodyPart: 'stretch', equipment: 'bodyweight', subCategory: 'full-stretch',
      tips: ['核心收紧，控制动作节奏', '全程保持稳定'],
      sets: 2, reps: '每侧5', rpe: 3, restSeconds: 20, isBodyweight: true, isStretch: true, isWarmup: true, difficulty: 'beginner', targetMuscles: ['目标拉伸肌群'],
      demoUrl: '/media/gifs/home-worlds-greatest-stretch',
      videoUrl: '/media/videos/home-worlds-greatest-stretch',
    },
    {
      id: 'home-side-plank-v2',
      name: '侧支撑',
      bodyPart: 'abs', equipment: 'bodyweight', subCategory: 'core',
      tips: ['核心收紧，控制动作节奏', '全程保持稳定'],
      sets: 3, reps: '每侧30秒', rpe: 8, restSeconds: 60, isBodyweight: true, difficulty: 'advanced', targetMuscles: ['核心肌群', '腹横肌'],
      demoUrl: '/media/gifs/home-side-plank-v2',
      videoUrl: '/media/videos/home-side-plank-v2',
    },
    {
      id: 'home-bulgarian-split-squat-v2',
      name: '保加利亚分腿蹲',
      bodyPart: 'legs', equipment: 'dumbbell', subCategory: 'glutes',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 3, reps: '每侧10', rpe: 8, restSeconds: 75, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['臀大肌', '腘绳肌'],
      demoUrl: '/media/gifs/home-bulgarian-split-squat-v2',
      videoUrl: '/media/videos/home-bulgarian-split-squat-v2',
    },
    {
      id: 'home-glute-bridge-v2',
      name: '臀桥',
      bodyPart: 'legs', equipment: 'bodyweight', subCategory: 'glutes',
      tips: ['核心收紧，控制动作节奏', '全程保持稳定'],
      sets: 4, reps: '15-20', rpe: 8, restSeconds: 60, isBodyweight: true, difficulty: 'advanced', targetMuscles: ['臀大肌', '腘绳肌'],
      demoUrl: '/media/gifs/home-glute-bridge-v2',
      videoUrl: '/media/videos/home-glute-bridge-v2',
    },
    {
      id: 'home-goblet-squat',
      name: '高脚杯深蹲',
      bodyPart: 'legs', equipment: 'dumbbell', subCategory: 'quadriceps',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 4, reps: '12-15', rpe: 8, restSeconds: 75, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['股四头肌', '臀大肌'],
      demoUrl: '/media/gifs/home-goblet-squat',
      videoUrl: '/media/videos/home-goblet-squat',
    },
    // --- 第4期（肩/臂混合） ---
    {
      id: 'home-prone-y-raise',
      name: '俯卧Y字举',
      bodyPart: 'shoulders', equipment: 'bodyweight', subCategory: 'rear-deltoid',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 2, reps: '15', rpe: 4, restSeconds: 30, isBodyweight: true, isWarmup: true, difficulty: 'beginner', targetMuscles: ['三角肌后束', '斜方肌下部'],
      demoUrl: '/media/gifs/home-prone-y-raise',
      videoUrl: '/media/videos/home-prone-y-raise',
    },
    {
      id: 'home-seated-dumbbell-rear-fly',
      name: '坐姿哑铃俯身飞鸟',
      bodyPart: 'shoulders', equipment: 'dumbbell', subCategory: 'rear-deltoid',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['三角肌后束', '斜方肌下部'],
      demoUrl: '/media/gifs/home-seated-dumbbell-rear-fly',
      videoUrl: '/media/videos/home-seated-dumbbell-rear-fly',
    },
    {
      id: 'home-seated-dumbbell-press',
      name: '坐姿哑铃推举',
      bodyPart: 'shoulders', equipment: 'dumbbell', subCategory: 'front-deltoid',
      tips: ['核心收紧，控制动作节奏', '全程保持肩胛骨稳定'],
      sets: 4, reps: '8-12', rpe: 8, restSeconds: 75, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['三角肌前束', '三角肌中束'],
      demoUrl: '/media/gifs/home-seated-dumbbell-press',
      videoUrl: '/media/videos/home-seated-dumbbell-press',
    },

  // ========== 凯圣王合集 · 35 个动作（独立 ksw- 前缀 ID） ==========
  // --- 第1期（胸肩三头）正式动作 ---
  {
    id: 'ksw-chest-bench-press-compressed',
    name: '杠铃卧推(compressed版)',
    bodyPart: 'chest', equipment: 'barbell', subCategory: 'mid-chest',
    tips: ['脚踩稳地面，核心收紧', '下放至胸中部，推起时不锁死肘关节'],
    sets: 4, reps: '5', rpe: 9, restSeconds: 120, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['胸大肌', '三角肌前束'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-chest-bench-press-compressed',
    videoUrl: '/media/videos/ksw-chest-bench-press-compressed',
  },
  {
    id: 'ksw-dips',
    name: '双杠臂屈伸',
    bodyPart: 'chest', equipment: 'bodyweight', subCategory: 'lower-chest',
    tips: ['身体前倾重心前移，侧重下胸', '下放至大臂平行于地面或略低'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 90, isBodyweight: true, difficulty: 'advanced', targetMuscles: ['下胸肌', '胸大肌'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-dips',
    videoUrl: '/media/videos/ksw-dips',
  },
  {
    id: 'ksw-lying-extension',
    name: '仰卧屈伸',
    bodyPart: 'arms', equipment: 'bodyweight', subCategory: 'triceps',
    tips: ['仰卧位，大臂固定垂直地面', '只动小臂，感受肱三头肌充分收缩'],
    sets: 3, reps: '15', rpe: 8, restSeconds: 60, isBodyweight: true, difficulty: 'advanced', targetMuscles: ['肱三头肌', '肘肌'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-lying-extension',
    videoUrl: '/media/videos/ksw-lying-extension',
  },
  {
    id: 'ksw-y-raise',
    name: 'Y字侧平举',
    bodyPart: 'shoulders', equipment: 'bodyweight', subCategory: 'rear-deltoid',
    tips: ['俯身或俯卧，手臂呈Y字展开', '用三角肌后束发力，10次+10次递减组'],
    sets: 3, reps: '10+10', rpe: 8, restSeconds: 60, isBodyweight: true, difficulty: 'advanced', targetMuscles: ['三角肌后束', '斜方肌下部'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-y-raise',
    videoUrl: '/media/videos/ksw-y-raise',
  },
  // --- 第1期 热身动作 ---
  {
    id: 'ksw-warmup-pec-minor-release',
    name: '胸小肌放松',
    bodyPart: 'chest', equipment: 'foam-roller', subCategory: '放松',
    tips: ['泡沫轴放胸小肌处，缓慢滚动找酸痛点停留呼吸', '每侧各做 1-2 分钟'],
    sets: 1, reps: '1-2分钟', rpe: 2, restSeconds: 0, isBodyweight: false, isWarmup: true, difficulty: 'beginner', targetMuscles: ['全身激活肌群'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-warmup-pec-minor-release',
    videoUrl: '/media/videos/ksw-warmup-pec-minor-release',
  },
  {
    id: 'ksw-warmup-pec-major-release',
    name: '胸大肌放松',
    bodyPart: 'chest', equipment: 'foam-roller', subCategory: '放松',
    tips: ['侧卧，泡沫轴放胸大肌位置滚动', '找到紧张点停留 20-30 秒'],
    sets: 1, reps: '1-2分钟', rpe: 2, restSeconds: 0, isBodyweight: false, isWarmup: true, difficulty: 'beginner', targetMuscles: ['全身激活肌群'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-warmup-pec-major-release',
    videoUrl: '/media/videos/ksw-warmup-pec-major-release',
  },
  {
    id: 'ksw-warmup-biceps-release',
    name: '二头肌放松',
    bodyPart: 'arms', equipment: 'foam-roller', subCategory: '放松',
    tips: ['泡沫轴放上臂前侧二头肌位置', '缓慢滚动，放松紧张的二头和前臂'],
    sets: 1, reps: '1-2分钟', rpe: 2, restSeconds: 0, isBodyweight: false, isWarmup: true, difficulty: 'beginner', targetMuscles: ['全身激活肌群'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-warmup-biceps-release',
    videoUrl: '/media/videos/ksw-warmup-biceps-release',
  },
  {
    id: 'ksw-warmup-lower-trap-y',
    name: '下斜方激活Y字',
    bodyPart: 'back', equipment: 'bodyweight', subCategory: '激活',
    tips: ['俯身成Y字，拇指朝上抬臂', '感受下斜方肌收紧，沉肩不耸肩'],
    sets: 2, reps: '15', rpe: 3, restSeconds: 30, isBodyweight: true, isWarmup: true, difficulty: 'beginner', targetMuscles: ['全身激活肌群'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-warmup-lower-trap-y',
    videoUrl: '/media/videos/ksw-warmup-lower-trap-y',
  },
  {
    id: 'ksw-warmup-serratus-activation',
    name: '前锯肌激活',
    bodyPart: 'chest', equipment: 'bodyweight', subCategory: '激活',
    tips: ['俯卧撑位，肩胛骨前引再收回', '感受前锯肌发力，核心稳定不塌腰'],
    sets: 2, reps: '15', rpe: 3, restSeconds: 30, isBodyweight: true, isWarmup: true, difficulty: 'beginner', targetMuscles: ['全身激活肌群'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-warmup-serratus-activation',
    videoUrl: '/media/videos/ksw-warmup-serratus-activation',
  },
  {
    id: 'ksw-warmup-wrist-activation',
    name: '手腕激活',
    bodyPart: 'warmup', equipment: 'bodyweight', subCategory: '激活',
    tips: ['手腕做前后左右环绕+屈伸', '充分活动腕关节，预防卧推受伤'],
    sets: 2, reps: '15', rpe: 2, restSeconds: 20, isBodyweight: true, isWarmup: true, difficulty: 'beginner', targetMuscles: ['全身激活肌群'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-warmup-wrist-activation',
    videoUrl: '/media/videos/ksw-warmup-wrist-activation',
  },
  // --- 第2期（背+后束+二头）正式动作 ---
  {
    id: 'ksw-single-arm-cable-pulldown',
    name: '单手钢线下拉',
    bodyPart: 'back', equipment: 'cable', subCategory: 'lat',
    tips: ['单手持把手，挺胸沉肩', '下压时感受背阔肌收缩，顶峰停顿 1 秒'],
    sets: 3, reps: '12', rpe: 8, restSeconds: 75, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['背阔肌', '大圆肌'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-single-arm-cable-pulldown',
    videoUrl: '/media/videos/ksw-single-arm-cable-pulldown',
  },
  {
    id: 'ksw-single-arm-machine-row',
    name: '单手器械划船',
    bodyPart: 'back', equipment: 'machine', subCategory: 'row',
    tips: ['单手对握把手，身体稳定不晃动', '拉至腰侧，感受中背收缩'],
    sets: 3, reps: '10', rpe: 8, restSeconds: 75, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['斜方肌中下部', '菱形肌', '背阔肌'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-single-arm-machine-row',
    videoUrl: '/media/videos/ksw-single-arm-machine-row',
  },
  {
    id: 'ksw-wide-grip-row',
    name: '开肘拉',
    bodyPart: 'back', equipment: 'cable', subCategory: 'rear-delt',
    tips: ['开肘位绳索划船，侧重后束和中背', '拉至下巴位置，肩胛骨充分后缩'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['三角肌后束', '斜方肌'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-wide-grip-row',
    videoUrl: '/media/videos/ksw-wide-grip-row',
  },
  {
    id: 'ksw-cable-curl',
    name: '钢线弯举',
    bodyPart: 'arms', equipment: 'cable', subCategory: 'biceps',
    tips: ['身体稳定，大臂贴身体两侧', '弯举时感受二头肌顶峰收缩，下放控制'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['肱二头肌', '肱肌'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-cable-curl',
    videoUrl: '/media/videos/ksw-cable-curl',
  },
  // --- 第2期 热身动作 ---
  {
    id: 'ksw-warmup-band-shoulder-circles',
    name: '弹力带绕肩',
    bodyPart: 'warmup', equipment: 'resistance-band', subCategory: '激活',
    tips: ['弹力带两端握手上，直臂前后绕肩', '动作缓慢流畅，激活肩关节活动度'],
    sets: 2, reps: '15', rpe: 3, restSeconds: 30, isBodyweight: false, isWarmup: true, difficulty: 'beginner', targetMuscles: ['全身激活肌群'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-warmup-band-shoulder-circles',
    videoUrl: '/media/videos/ksw-warmup-band-shoulder-circles',
  },
  {
    id: 'ksw-warmup-thoracic-flexion',
    name: '胸椎屈曲激活',
    bodyPart: 'warmup', equipment: 'bodyweight', subCategory: '激活',
    tips: ['跪位或站姿，做胸椎屈曲-伸展', '打开胸椎活动度，提升划船动作质量'],
    sets: 2, reps: '10', rpe: 2, restSeconds: 20, isBodyweight: true, isWarmup: true, difficulty: 'beginner', targetMuscles: ['全身激活肌群'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-warmup-thoracic-flexion',
    videoUrl: '/media/videos/ksw-warmup-thoracic-flexion',
  },
  {
    id: 'ksw-warmup-thoracic-proprioception',
    name: '胸椎本体感受激活',
    bodyPart: 'shoulders', equipment: 'bodyweight', subCategory: '激活',
    tips: ['配合呼吸做胸椎旋转+稳定', '提升胸椎本体感受，减少腰背代偿'],
    sets: 2, reps: '10', rpe: 2, restSeconds: 20, isBodyweight: true, isWarmup: true, difficulty: 'beginner', targetMuscles: ['全身激活肌群'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-warmup-thoracic-proprioception',
    videoUrl: '/media/videos/ksw-warmup-thoracic-proprioception',
  },
  // --- 第3期 热身：伟大者伸展（独立实例，与第2期不同） ---
  // --- 第3期（腿）正式动作 ---
  {
    id: 'ksw-single-leg-bulgarian',
    name: '单腿保加利亚蹲',
    bodyPart: 'legs', equipment: 'bodyweight', subCategory: 'quads-glutes',
    tips: ['后脚高架在凳上，前腿下蹲', '膝盖对准脚尖，重心在前脚脚跟'],
    sets: 3, reps: '10', rpe: 8, restSeconds: 75, isBodyweight: true, difficulty: 'advanced', targetMuscles: ['股四头肌', '臀大肌'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-single-leg-bulgarian',
    videoUrl: '/media/videos/ksw-single-leg-bulgarian',
  },
  {
    id: 'ksw-barbell-rdl',
    name: '杠铃罗马尼亚硬拉',
    bodyPart: 'legs', equipment: 'barbell', subCategory: 'hamstrings-glutes',
    tips: ['髋部后推，杠铃沿大腿前侧下放', '感受腘绳肌强烈拉伸，臀部发力拉起'],
    sets: 3, reps: '15', rpe: 8, restSeconds: 90, isBodyweight: false, difficulty: 'advanced', targetMuscles: ['腘绳肌', '臀大肌'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-barbell-rdl',
    videoUrl: '/media/videos/ksw-barbell-rdl',
  },
  {
    id: 'ksw-back-extension',
    name: '山羊挺身',
    bodyPart: 'legs', equipment: 'bodyweight', subCategory: 'lower-back',
    tips: ['大腿前侧贴垫，髋部折叠下俯', '起身时感受下背和臀部收紧，不超伸'],
    sets: 3, reps: '8', rpe: 7, restSeconds: 60, isBodyweight: true, difficulty: 'intermediate', targetMuscles: ['竖脊肌', '下背部肌群'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-back-extension',
    videoUrl: '/media/videos/ksw-back-extension',
  },
  // --- 第3期 热身动作 ---
  {
    id: 'ksw-warmup-dynamic-frog',
    name: '动态青蛙趴',
    bodyPart: 'legs', equipment: 'bodyweight', subCategory: '髋',
    tips: ['蛙式下蹲+动态弹动，打开髋内收', '膝盖打开对准脚尖，重心下沉'],
    sets: 2, reps: '15', rpe: 2, restSeconds: 20, isBodyweight: true, isWarmup: true, difficulty: 'beginner', targetMuscles: ['全身激活肌群'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-warmup-dynamic-frog',
    videoUrl: '/media/videos/ksw-warmup-dynamic-frog',
  },
  {
    id: 'ksw-warmup-hip-90-90',
    name: '髋关节90/90',
    bodyPart: 'legs', equipment: 'bodyweight', subCategory: '髋',
    tips: ['坐姿两腿各 90°，身体前后倾', '打开髋内旋和外旋活动度，每侧各 10 次'],
    sets: 2, reps: '每侧10', rpe: 2, restSeconds: 20, isBodyweight: true, isWarmup: true, difficulty: 'beginner', targetMuscles: ['全身激活肌群'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-warmup-hip-90-90',
    videoUrl: '/media/videos/ksw-warmup-hip-90-90',
  },
  {
    id: 'ksw-warmup-ankle-activation',
    name: '脚踝激活',
    bodyPart: 'warmup', equipment: 'bodyweight', subCategory: '激活',
    tips: ['脚踝做环绕+提踵+内外翻', '充分活动踝关节，提升深蹲稳定性'],
    sets: 2, reps: '15', rpe: 2, restSeconds: 20, isBodyweight: true, isWarmup: true, difficulty: 'beginner', targetMuscles: ['全身激活肌群'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-warmup-ankle-activation',
    videoUrl: '/media/videos/ksw-warmup-ankle-activation',
  },
  {
    id: 'ksw-warmup-foam-calf',
    name: '泡沫轴松解-小腿',
    bodyPart: 'legs', equipment: 'foam-roller', subCategory: '松解',
    tips: ['泡沫轴放小腿下方，缓慢滚动', '找到酸痛点停留 20-30 秒'],
    sets: 1, reps: '1-2分钟', rpe: 2, restSeconds: 0, isBodyweight: false, isWarmup: true, difficulty: 'beginner', targetMuscles: ['全身激活肌群'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-warmup-foam-calf',
    videoUrl: '/media/videos/ksw-warmup-foam-calf',
  },
  {
    id: 'ksw-warmup-foam-quad',
    name: '泡沫轴松解-大腿',
    bodyPart: 'legs', equipment: 'foam-roller', subCategory: '松解',
    tips: ['俯卧，泡沫轴放大腿前侧', '股四头肌全面滚动松解，1-2 分钟'],
    sets: 1, reps: '1-2分钟', rpe: 2, restSeconds: 0, isBodyweight: false, isWarmup: true, difficulty: 'beginner', targetMuscles: ['全身激活肌群'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-warmup-foam-quad',
    videoUrl: '/media/videos/ksw-warmup-foam-quad',
  },
  {
    id: 'ksw-warmup-foam-glute',
    name: '泡沫轴松解-屁股',
    bodyPart: 'legs', equipment: 'foam-roller', subCategory: '松解',
    tips: ['坐泡沫轴上，侧偏臀部滚动', '松解臀大肌和臀中肌，1-2 分钟'],
    sets: 1, reps: '1-2分钟', rpe: 2, restSeconds: 0, isBodyweight: false, isWarmup: true, difficulty: 'beginner', targetMuscles: ['全身激活肌群'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-warmup-foam-glute',
    videoUrl: '/media/videos/ksw-warmup-foam-glute',
  },
  {
    id: 'ksw-warmup-foam-tfl',
    name: '泡沫轴松解-阔筋膜张肌',
    bodyPart: 'legs', equipment: 'foam-roller', subCategory: '松解',
    tips: ['侧卧，泡沫轴放髋外侧阔筋膜张肌', '缓慢滚动，紧张点停留呼吸'],
    sets: 1, reps: '1-2分钟', rpe: 2, restSeconds: 0, isBodyweight: false, isWarmup: true, difficulty: 'beginner', targetMuscles: ['全身激活肌群'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-warmup-foam-tfl',
    videoUrl: '/media/videos/ksw-warmup-foam-tfl',
  },
  {
    id: 'ksw-warmup-foam-tibialis',
    name: '泡沫轴松解-胫骨前肌',
    bodyPart: 'legs', equipment: 'foam-roller', subCategory: '松解',
    tips: ['小腿前侧胫骨前肌放泡沫轴上', '缓慢滚动，放松胫骨前肌'],
    sets: 1, reps: '1-2分钟', rpe: 2, restSeconds: 0, isBodyweight: false, isWarmup: true, difficulty: 'beginner', targetMuscles: ['全身激活肌群'],
    source: '凯圣王合集',
    demoUrl: '/media/gifs/ksw-warmup-foam-tibialis',
    videoUrl: '/media/videos/ksw-warmup-foam-tibialis',
  },
];

export function getExercisesByBodyPart(part: BodyPart): IExercise[] {
  return getAllExercisesEnriched().filter(e => e.bodyPart === part);
}

export function getExerciseById(id: string): IExercise | undefined {
  return getAllExercisesEnriched().find(e => e.id === id);
}

export function getAllExercises(): IExercise[] {
  return getAllExercisesEnriched();
}

// ========== 三分化训练合集 A（26个动作，threeway- 前缀） ==========
const THREEWAY_EXERCISES: IExercise[] = [
  // Day1 推日 热身
  {
    id: 'threeway-shoulder-warmup',
    name: '肩部热身',
    bodyPart: 'shoulders', equipment: 'bodyweight', subCategory: 'shoulder',
    tips: ['肩部环绕激活肩袖肌群', '前后各10圈'],
    sets: 2, reps: '15次', rpe: 5, restSeconds: 30, isBodyweight: true, isWarmup: true,
    demoUrl: '/media/videos/threeway-shoulder-warmup',
    videoUrl: '/media/videos/threeway-shoulder-warmup',
  },
  // Day1 推日 正式
  {
    id: 'threeway-machine-fly',
    name: '坐姿夹胸',
    bodyPart: 'chest', equipment: 'machine', subCategory: 'mid-chest',
    tips: ['顶峰收缩1秒', '控制回放速度'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: false,
    demoUrl: '/media/videos/threeway-machine-fly',
    videoUrl: '/media/videos/threeway-machine-fly',
  },
  {
    id: 'threeway-flat-machine-press',
    name: '平板器械卧推',
    bodyPart: 'chest', equipment: 'machine', subCategory: 'mid-chest',
    tips: ['肩胛骨收紧下沉', '推到顶端不锁肘'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 75, isBodyweight: false,
    demoUrl: '/media/videos/threeway-flat-machine-press',
    videoUrl: '/media/videos/threeway-flat-machine-press',
  },
  {
    id: 'threeway-dips',
    name: '双杠臂屈伸',
    bodyPart: 'chest', equipment: 'bodyweight', subCategory: 'lower-chest',
    tips: ['身体前倾刺激下胸', '下放至大臂平行'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: true,
    demoUrl: '/media/videos/threeway-dips',
    videoUrl: '/media/videos/threeway-dips',
  },
  {
    id: 'threeway-decline-cable-fly',
    name: '绳索夹下胸',
    bodyPart: 'chest', equipment: 'cable', subCategory: 'lower-chest',
    tips: ['从高往低夹', '顶峰收缩挤压下胸'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: false,
    demoUrl: '/media/videos/threeway-decline-cable-fly',
    videoUrl: '/media/videos/threeway-decline-cable-fly',
  },
  {
    id: 'threeway-lateral-raise',
    name: '侧平举',
    bodyPart: 'shoulders', equipment: 'dumbbell', subCategory: 'side-deltoid',
    tips: ['肘部微屈', '举至与肩同高'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 45, isBodyweight: false,
    demoUrl: '/media/videos/threeway-lateral-raise',
    videoUrl: '/media/videos/threeway-lateral-raise',
  },
  {
    id: 'threeway-behind-neck-tricep',
    name: '颈后臂屈伸',
    bodyPart: 'arms', equipment: 'bodyweight', subCategory: 'triceps',
    tips: ['手肘尽量贴近耳朵', '下落至小臂平行'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 45, isBodyweight: true,
    demoUrl: '/media/videos/threeway-behind-neck-tricep',
    videoUrl: '/media/videos/threeway-behind-neck-tricep',
  },
  // Day2 拉日 热身
  {
    id: 'threeway-back-warmup',
    name: '背部热身',
    bodyPart: 'back', equipment: 'bodyweight', subCategory: 'full-body',
    tips: ['激活背阔肌和菱形肌', '每组15次'],
    sets: 2, reps: '15次', rpe: 5, restSeconds: 30, isBodyweight: true, isWarmup: true,
    demoUrl: '/media/videos/threeway-back-warmup',
    videoUrl: '/media/videos/threeway-back-warmup',
  },
  // Day2 拉日 正式
  {
    id: 'threeway-neutral-pulldown',
    name: '对握高位下拉',
    bodyPart: 'back', equipment: 'cable', subCategory: 'lat',
    tips: ['拉至胸上沿', '挤压背阔肌'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 75, isBodyweight: false,
    demoUrl: '/media/videos/threeway-neutral-pulldown',
    videoUrl: '/media/videos/threeway-neutral-pulldown',
  },
  {
    id: 'threeway-supinated-pulldown',
    name: '反手高位下拉',
    bodyPart: 'back', equipment: 'cable', subCategory: 'lat',
    tips: ['反手握距较窄', '二头协同发力'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 75, isBodyweight: false,
    demoUrl: '/media/videos/threeway-supinated-pulldown',
    videoUrl: '/media/videos/threeway-supinated-pulldown',
  },
  {
    id: 'threeway-narrow-cable-row',
    name: '窄距对握划船',
    bodyPart: 'back', equipment: 'cable', subCategory: 'row',
    tips: ['拉至下腹部', '肩胛骨后缩'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 75, isBodyweight: false,
    demoUrl: '/media/videos/threeway-narrow-cable-row',
    videoUrl: '/media/videos/threeway-narrow-cable-row',
  },
  {
    id: 'threeway-tbar-row',
    name: 'T杆划船',
    bodyPart: 'back', equipment: 'barbell', subCategory: 'row',
    tips: ['核心稳定不晃', '拉至髋部'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 75, isBodyweight: false,
    demoUrl: '/media/videos/threeway-tbar-row',
    videoUrl: '/media/videos/threeway-tbar-row',
  },
  {
    id: 'threeway-seated-reverse-fly',
    name: '坐姿反飞鸟',
    bodyPart: 'shoulders', equipment: 'machine', subCategory: 'rear-deltoid',
    tips: ['肩胛骨后缩', '感受后束收缩'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 45, isBodyweight: false,
    demoUrl: '/media/videos/threeway-seated-reverse-fly',
    videoUrl: '/media/videos/threeway-seated-reverse-fly',
  },
  {
    id: 'threeway-cable-reverse-fly',
    name: '对握反飞鸟',
    bodyPart: 'shoulders', equipment: 'cable', subCategory: 'rear-deltoid',
    tips: ['手肘微屈', '绳索从身前向两侧打开'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 45, isBodyweight: false,
    demoUrl: '/media/videos/threeway-cable-reverse-fly',
    videoUrl: '/media/videos/threeway-cable-reverse-fly',
  },
  {
    id: 'threeway-bench-dip',
    name: '凳上反屈伸',
    bodyPart: 'arms', equipment: 'bodyweight', subCategory: 'triceps',
    tips: ['手肘贴近身体', '下放至大臂平行'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 45, isBodyweight: true,
    demoUrl: '/media/videos/threeway-bench-dip',
    videoUrl: '/media/videos/threeway-bench-dip',
  },
  {
    id: 'threeway-barbell-skull-crusher',
    name: '仰卧杠铃臂屈伸',
    bodyPart: 'arms', equipment: 'barbell', subCategory: 'triceps',
    tips: ['大臂保持垂直', '曲杆杠铃更护肘'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 45, isBodyweight: false,
    demoUrl: '/media/videos/threeway-barbell-skull-crusher',
    videoUrl: '/media/videos/threeway-barbell-skull-crusher',
  },
  // Day3 蹲日 热身
  {
    id: 'threeway-dumbbell-shoulder-activation',
    name: '哑铃肩激活',
    bodyPart: 'shoulders', equipment: 'dumbbell', subCategory: 'shoulder',
    tips: ['轻重量多组数激活', '侧平举+前平举组合'],
    sets: 2, reps: '15次', rpe: 5, restSeconds: 30, isBodyweight: false, isWarmup: true,
    demoUrl: '/media/videos/threeway-dumbbell-shoulder-activation',
    videoUrl: '/media/videos/threeway-dumbbell-shoulder-activation',
  },
  // Day3 蹲日 正式
  {
    id: 'threeway-smith-press',
    name: '史密斯实力推',
    bodyPart: 'shoulders', equipment: 'machine', subCategory: 'front-deltoid',
    tips: ['史密斯轨迹稳定', '推至头顶不锁肘'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 75, isBodyweight: false,
    demoUrl: '/media/videos/threeway-smith-press',
    videoUrl: '/media/videos/threeway-smith-press',
  },
  {
    id: 'threeway-bent-barbell-row',
    name: '俯身杠铃提拉',
    bodyPart: 'back', equipment: 'barbell', subCategory: 'row',
    tips: ['俯身角度45度', '杠铃沿小腿向上提拉'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 75, isBodyweight: false,
    demoUrl: '/media/videos/threeway-bent-barbell-row',
    videoUrl: '/media/videos/threeway-bent-barbell-row',
  },
  {
    id: 'threeway-lying-leg-curl',
    name: '俯身腿弯举',
    bodyPart: 'legs', equipment: 'machine', subCategory: 'hamstrings',
    tips: ['臀部贴紧凳面', '感受腘绳肌收缩'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: false,
    demoUrl: '/media/videos/threeway-lying-leg-curl',
    videoUrl: '/media/videos/threeway-lying-leg-curl',
  },
  {
    id: 'threeway-rhino-squat',
    name: '犀牛蹲',
    bodyPart: 'legs', equipment: 'bodyweight', subCategory: 'quads',
    tips: ['重心前移', '膝盖朝脚尖方向'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: true,
    demoUrl: '/media/videos/threeway-rhino-squat',
    videoUrl: '/media/videos/threeway-rhino-squat',
  },
  {
    id: 'threeway-bulgarian-squat',
    name: '保加利亚蹲',
    bodyPart: 'legs', equipment: 'bodyweight', subCategory: 'quads-glutes',
    tips: ['前腿承重为主', '下蹲至大腿平行'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: true,
    demoUrl: '/media/videos/threeway-bulgarian-squat',
    videoUrl: '/media/videos/threeway-bulgarian-squat',
  },
];

// 将三分化动作并入总库
THREEWAY_EXERCISES.forEach(e => MOCK_EXERCISES.push(e));

// ========== 私教跟练合集（8期·56个动作，personal- 前缀） ==========
const PERSONAL_EXERCISES: IExercise[] = [
  // --- 第1期 全身完整拉伸（15个·全部拉伸） ---
  { id: 'personal-s1-chest-stretch', name: '胸大肌拉伸', bodyPart: 'chest', equipment: 'bodyweight', subCategory: 'chest', tips: ['保持挺胸，感受胸肌拉伸', '每侧保持30-60秒'], sets: 2, reps: '45秒', rpe: 2, restSeconds: 10, isBodyweight: true, isStretch: true, isWarmup: true, source: '私教版', demoUrl: '/media/gifs/personal-s1-chest-stretch', videoUrl: '/media/videos/personal-s1-chest-stretch' },
  { id: 'personal-s1-posterior-delt-stretch', name: '三角肌后束拉伸', bodyPart: 'shoulders', equipment: 'bodyweight', subCategory: 'shoulder', tips: ['肩膀下沉不耸肩', '每侧保持30-60秒'], sets: 2, reps: '45秒', rpe: 2, restSeconds: 10, isBodyweight: true, isStretch: true, isWarmup: true, source: '私教版', demoUrl: '/media/gifs/personal-s1-posterior-delt-stretch', videoUrl: '/media/videos/personal-s1-posterior-delt-stretch' },
  { id: 'personal-s1-anterior-delt-stretch', name: '三角肌前束拉伸', bodyPart: 'shoulders', equipment: 'bodyweight', subCategory: 'shoulder', tips: ['核心收紧不塌腰', '每侧保持30-60秒'], sets: 2, reps: '45秒', rpe: 2, restSeconds: 10, isBodyweight: true, isStretch: true, isWarmup: true, source: '私教版', demoUrl: '/media/gifs/personal-s1-anterior-delt-stretch', videoUrl: '/media/videos/personal-s1-anterior-delt-stretch' },
  { id: 'personal-s1-back-tricep-stretch', name: '背加三头拉伸', bodyPart: 'arms', equipment: 'bodyweight', subCategory: 'back', tips: ['感受背阔肌与三头肌的延展', '每侧保持30-60秒'], sets: 2, reps: '45秒', rpe: 2, restSeconds: 10, isBodyweight: true, isStretch: true, isWarmup: true, source: '私教版', demoUrl: '/media/gifs/personal-s1-back-tricep-stretch', videoUrl: '/media/videos/personal-s1-back-tricep-stretch' },
  { id: 'personal-s1-rhomboid-stretch', name: '菱形肌拉伸', bodyPart: 'back', equipment: 'bodyweight', subCategory: 'back', tips: ['含胸沉肩，感受中背拉伸', '保持30-60秒'], sets: 2, reps: '45秒', rpe: 2, restSeconds: 10, isBodyweight: true, isStretch: true, isWarmup: true, source: '私教版', demoUrl: '/media/gifs/personal-s1-rhomboid-stretch', videoUrl: '/media/videos/personal-s1-rhomboid-stretch' },
  { id: 'personal-s1-bicep-stretch', name: '二头肌拉伸', bodyPart: 'arms', equipment: 'bodyweight', subCategory: 'arms', tips: ['掌心朝外，感受二头延展', '每侧保持30-60秒'], sets: 2, reps: '45秒', rpe: 2, restSeconds: 10, isBodyweight: true, isStretch: true, isWarmup: true, source: '私教版', demoUrl: '/media/gifs/personal-s1-bicep-stretch', videoUrl: '/media/videos/personal-s1-bicep-stretch' },
  { id: 'personal-s1-abs-oblique-stretch', name: '腹肌加腹外斜肌拉伸', bodyPart: 'abs', equipment: 'bodyweight', subCategory: 'abs', tips: ['侧屈幅度循序渐进', '每侧保持30-60秒'], sets: 2, reps: '45秒', rpe: 2, restSeconds: 10, isBodyweight: true, isStretch: true, isWarmup: true, source: '私教版', demoUrl: '/media/gifs/personal-s1-abs-oblique-stretch', videoUrl: '/media/videos/personal-s1-abs-oblique-stretch' },
  { id: 'personal-s1-glute-stretch', name: '臀大肌拉伸', bodyPart: 'legs', equipment: 'bodyweight', subCategory: 'glutes', tips: ['腰背平直，臀部后坐', '每侧保持30-60秒'], sets: 2, reps: '45秒', rpe: 2, restSeconds: 10, isBodyweight: true, isStretch: true, isWarmup: true, source: '私教版', demoUrl: '/media/gifs/personal-s1-glute-stretch', videoUrl: '/media/videos/personal-s1-glute-stretch' },
  { id: 'personal-s1-hamstring-stretch', name: '大腿后侧拉伸', bodyPart: 'legs', equipment: 'bodyweight', subCategory: 'legs', tips: ['膝盖微屈不过伸', '每侧保持30-60秒'], sets: 2, reps: '45秒', rpe: 2, restSeconds: 10, isBodyweight: true, isStretch: true, isWarmup: true, source: '私教版', demoUrl: '/media/gifs/personal-s1-hamstring-stretch', videoUrl: '/media/videos/personal-s1-hamstring-stretch' },
  { id: 'personal-s1-quad-stretch', name: '大腿前侧拉伸', bodyPart: 'legs', equipment: 'bodyweight', subCategory: 'legs', tips: ['膝盖指向地面不外翻', '每侧保持30-60秒'], sets: 2, reps: '45秒', rpe: 2, restSeconds: 10, isBodyweight: true, isStretch: true, isWarmup: true, source: '私教版', demoUrl: '/media/gifs/personal-s1-quad-stretch', videoUrl: '/media/videos/personal-s1-quad-stretch' },
  { id: 'personal-s1-calf-stretch', name: '小腿拉伸', bodyPart: 'legs', equipment: 'bodyweight', subCategory: 'calves', tips: ['脚跟踩实，感受小腿后侧拉伸', '每侧保持30-60秒'], sets: 2, reps: '45秒', rpe: 2, restSeconds: 10, isBodyweight: true, isStretch: true, isWarmup: true, source: '私教版', demoUrl: '/media/gifs/personal-s1-calf-stretch', videoUrl: '/media/videos/personal-s1-calf-stretch' },
  { id: 'personal-s1-levator-scap-stretch', name: '肩胛提肌拉伸', bodyPart: 'stretch', equipment: 'bodyweight', subCategory: 'neck', tips: ['侧屈+旋转组合拉伸', '每侧保持30-60秒'], sets: 2, reps: '45秒', rpe: 2, restSeconds: 10, isBodyweight: true, isStretch: true, isWarmup: true, source: '私教版', demoUrl: '/media/gifs/personal-s1-levator-scap-stretch', videoUrl: '/media/videos/personal-s1-levator-scap-stretch' },
  { id: 'personal-s1-sternocleidomastoid', name: '胸锁乳突肌拉伸', bodyPart: 'stretch', equipment: 'bodyweight', subCategory: 'neck', tips: ['动作缓慢，感受颈部前侧肌肉延展', '每侧保持30-60秒'], sets: 2, reps: '45秒', rpe: 2, restSeconds: 10, isBodyweight: true, isStretch: true, isWarmup: true, source: '私教版', demoUrl: '/media/gifs/personal-s1-sternocleidomastoid', videoUrl: '/media/videos/personal-s1-sternocleidomastoid' },
  { id: 'personal-s1-scalene-release', name: '斜角肌松解', bodyPart: 'stretch', equipment: 'bodyweight', subCategory: 'neck', tips: ['用指腹轻柔按压松解', '每侧保持30-60秒'], sets: 2, reps: '45秒', rpe: 2, restSeconds: 10, isBodyweight: true, isStretch: true, isWarmup: true, source: '私教版', demoUrl: '/media/gifs/personal-s1-scalene-release', videoUrl: '/media/videos/personal-s1-scalene-release' },
  { id: 'personal-s1-platysma-stretch', name: '颈阔肌拉伸', bodyPart: 'stretch', equipment: 'bodyweight', subCategory: 'neck', tips: ['仰头伸展颈部前侧', '保持30-60秒'], sets: 2, reps: '45秒', rpe: 2, restSeconds: 10, isBodyweight: true, isStretch: true, isWarmup: true, source: '私教版', demoUrl: '/media/gifs/personal-s1-platysma-stretch', videoUrl: '/media/videos/personal-s1-platysma-stretch' },

  // --- 第2期 背部私教跟练（9个·01-03热身，04-09正式） ---
  { id: 'personal-s2-prep', name: '训练前准备', bodyPart: 'warmup', equipment: 'bodyweight', subCategory: 'general', tips: ['活动肩袖与胸椎', '为背部训练做准备'], sets: 1, reps: '3分钟', rpe: 3, restSeconds: 0, isBodyweight: true, isWarmup: true, source: '私教版', demoUrl: '/media/gifs/personal-s2-prep', videoUrl: '/media/videos/personal-s2-prep' },
  { id: 'personal-s2-ab-wheel-foam', name: '健腹轮泡沫轴', bodyPart: 'warmup', equipment: 'foam-roller', subCategory: 'core', tips: ['放松前侧核心肌群', '缓慢滚动不憋气'], sets: 1, reps: '2分钟', rpe: 3, restSeconds: 0, isBodyweight: false, isWarmup: true, source: '私教版', demoUrl: '/media/gifs/personal-s2-ab-wheel-foam', videoUrl: '/media/videos/personal-s2-ab-wheel-foam' },
  { id: 'personal-s2-straight-arm-pulldown', name: '直臂下压', bodyPart: 'back', equipment: 'cable', subCategory: 'lats', tips: ['核心收紧，背部主导发力', '感受背阔肌延展与收缩'], sets: 3, reps: '12', rpe: 7, restSeconds: 60, isBodyweight: false, source: '私教版', demoUrl: '/media/gifs/personal-s2-straight-arm-pulldown', videoUrl: '/media/videos/personal-s2-straight-arm-pulldown' },
  { id: 'personal-s2-single-arm-cable-row', name: '单手绳索划船', bodyPart: 'back', equipment: 'cable', subCategory: 'lats', tips: ['肩胛后收再拉肘', '每侧交替感受背阔肌收缩'], sets: 3, reps: '12', rpe: 8, restSeconds: 75, isBodyweight: false, source: '私教版', demoUrl: '/media/gifs/personal-s2-single-arm-cable-row', videoUrl: '/media/videos/personal-s2-single-arm-cable-row' },
  { id: 'personal-s2-cable-tricep-pushdown', name: '绳索曲臂下压', bodyPart: 'arms', equipment: 'cable', subCategory: 'triceps', tips: ['大臂固定不晃动', '感受三头肌收缩'], sets: 3, reps: '12', rpe: 8, restSeconds: 60, isBodyweight: false, source: '私教版', demoUrl: '/media/gifs/personal-s2-cable-tricep-pushdown', videoUrl: '/media/videos/personal-s2-cable-tricep-pushdown' },
  { id: 'personal-s2-cable-reverse-fly', name: '龙门架反向飞鸟', bodyPart: 'shoulders', equipment: 'cable', subCategory: 'rear-delt', tips: ['俯身角度稳定', '感受后束挤压'], sets: 3, reps: '15', rpe: 7, restSeconds: 45, isBodyweight: false, source: '私教版', demoUrl: '/media/gifs/personal-s2-cable-reverse-fly', videoUrl: '/media/videos/personal-s2-cable-reverse-fly' },

  // --- 第3期 手臂教学·一期（6个·全部正式，1热身+3正式组） ---
  { id: 'personal-s3-supine-dumbbell-curl', name: '仰卧哑铃肩伸位弯举', bodyPart: 'arms', equipment: 'dumbbell', subCategory: 'biceps', tips: ['仰卧肩伸位长头受力更多', '控制离心阶段', '1热身+3正式组'], sets: 3, reps: '10', rpe: 8, restSeconds: 75, isBodyweight: false, source: '私教版', demoUrl: '/media/gifs/personal-s3-supine-dumbbell-curl', videoUrl: '/media/videos/personal-s3-supine-dumbbell-curl' },
  { id: 'personal-s3-overhead-tricep', name: '单手绳索肩屈位臂屈伸', bodyPart: 'arms', equipment: 'cable', subCategory: 'triceps', tips: ['肩屈位长头受力更多', '大臂保持稳定', '1热身+3正式组'], sets: 3, reps: '12', rpe: 8, restSeconds: 60, isBodyweight: false, source: '私教版', demoUrl: '/media/gifs/personal-s3-overhead-tricep', videoUrl: '/media/videos/personal-s3-overhead-tricep' },
  { id: 'personal-s3-internal-rotation-curl', name: '坐姿绳索内旋位弯举', bodyPart: 'arms', equipment: 'cable', subCategory: 'biceps', tips: ['内旋位侧重肱肌', '控制动作节奏', '1热身+3正式组'], sets: 3, reps: '12', rpe: 8, restSeconds: 60, isBodyweight: false, source: '私教版', demoUrl: '/media/gifs/personal-s3-internal-rotation-curl', videoUrl: '/media/videos/personal-s3-internal-rotation-curl' },

  // --- 第4期 胸部教学（6个·01热身，其余正式） ---
  { id: 'personal-s4-seated-flat-press', name: '坐姿平推', bodyPart: 'chest', equipment: 'machine', subCategory: 'mid-chest', tips: ['挺胸沉肩肩胛后收', '推至胸口正上方'], sets: 3, reps: '10', rpe: 8, restSeconds: 75, isBodyweight: false, source: '私教版', demoUrl: '/media/gifs/personal-s4-seated-flat-press', videoUrl: '/media/videos/personal-s4-seated-flat-press' },
  { id: 'personal-s4-cable-upper-chest', name: '绳索夹上胸', bodyPart: 'chest', equipment: 'cable', subCategory: 'upper-chest', tips: ['由下往上夹胸', '顶峰收缩停顿1秒'], sets: 3, reps: '15', rpe: 7, restSeconds: 60, isBodyweight: false, source: '私教版', demoUrl: '/media/gifs/personal-s4-cable-upper-chest', videoUrl: '/media/videos/personal-s4-cable-upper-chest' },
  { id: 'personal-s4-shoulder-work', name: '肩部训练', bodyPart: 'shoulders', equipment: 'dumbbell', subCategory: 'medial-delt', tips: ['作为胸部训练收尾', '侧平举中束刺激'], sets: 3, reps: '12', rpe: 7, restSeconds: 60, isBodyweight: false, source: '私教版', demoUrl: '/media/gifs/personal-s4-shoulder-work', videoUrl: '/media/videos/personal-s4-shoulder-work' },

  // --- 第5期 肩部教学（7个·01-02热身，其余正式） ---
  { id: 'personal-s5-trx-activation', name: 'Trx激活', bodyPart: 'shoulders', equipment: 'trx', subCategory: 'shoulder', tips: ['激活肩胛稳定肌', '动作匀速控制'], sets: 2, reps: '12', rpe: 4, restSeconds: 30, isBodyweight: false, isWarmup: true, source: '私教版', demoUrl: '/media/gifs/personal-s5-trx-activation', videoUrl: '/media/videos/personal-s5-trx-activation' },
  { id: 'personal-s5-bent-over-rear-delt', name: '俯身哑铃肩伸后束', bodyPart: 'shoulders', equipment: 'dumbbell', subCategory: 'rear-delt', tips: ['俯身角度稳定不晃', '感受后束挤压'], sets: 3, reps: '15', rpe: 7, restSeconds: 45, isBodyweight: false, source: '私教版', demoUrl: '/media/gifs/personal-s5-bent-over-rear-delt', videoUrl: '/media/videos/personal-s5-bent-over-rear-delt' },
  { id: 'personal-s5-dumbbell-shoulder-press', name: '哑铃推肩', bodyPart: 'shoulders', equipment: 'dumbbell', subCategory: 'anterior-delt', tips: ['核心收紧不顶腰', '推至头顶正上方'], sets: 3, reps: '10', rpe: 8, restSeconds: 75, isBodyweight: false, source: '私教版', demoUrl: '/media/gifs/personal-s5-dumbbell-shoulder-press', videoUrl: '/media/videos/personal-s5-dumbbell-shoulder-press' },
  { id: 'personal-s5-lateral-raise', name: '哑铃侧平举中束', bodyPart: 'shoulders', equipment: 'dumbbell', subCategory: 'medial-delt', tips: ['肘微屈，侧抬与肩齐平', '控制下放'], sets: 3, reps: '15', rpe: 7, restSeconds: 45, isBodyweight: false, source: '私教版', demoUrl: '/media/gifs/personal-s5-lateral-raise', videoUrl: '/media/videos/personal-s5-lateral-raise' },

  // --- 第6期 腿部训练（7个·01热身，其余正式） ---
  { id: 'personal-s6-adductor', name: '内收肌训练', bodyPart: 'legs', equipment: 'machine', subCategory: 'inner-thigh', tips: ['内收时感受大腿内侧挤压', '控制回放'], sets: 3, reps: '12', rpe: 7, restSeconds: 60, isBodyweight: false, source: '私教版', demoUrl: '/media/gifs/personal-s6-adductor', videoUrl: '/media/videos/personal-s6-adductor' },
  { id: 'personal-s6-bulgarian-squat', name: '保加利亚深蹲', bodyPart: 'legs', equipment: 'dumbbell', subCategory: 'quads', tips: ['前腿膝盖不超过脚尖', '核心收紧保持平衡'], sets: 3, reps: '10', rpe: 8, restSeconds: 90, isBodyweight: false, source: '私教版', demoUrl: '/media/gifs/personal-s6-bulgarian-squat', videoUrl: '/media/videos/personal-s6-bulgarian-squat' },
  { id: 'personal-s6-dumbbell-rdl', name: '哑铃硬拉', bodyPart: 'legs', equipment: 'dumbbell', subCategory: 'hamstrings', tips: ['屈髋主导，感受腘绳肌延展', '腰背全程平直'], sets: 3, reps: '10', rpe: 8, restSeconds: 90, isBodyweight: false, source: '私教版', demoUrl: '/media/gifs/personal-s6-dumbbell-rdl', videoUrl: '/media/videos/personal-s6-dumbbell-rdl' },
  { id: 'personal-s6-hip-flexor-raise', name: '屈髋抬腿', bodyPart: 'legs', equipment: 'bodyweight', subCategory: 'hip-flexor', tips: ['核心收紧不塌腰', '感受髋屈肌发力'], sets: 3, reps: '12', rpe: 7, restSeconds: 60, isBodyweight: true, source: '私教版', demoUrl: '/media/gifs/personal-s6-hip-flexor-raise', videoUrl: '/media/videos/personal-s6-hip-flexor-raise' },

  // --- 第7期 腹肌教学（3个·全部正式） ---
  { id: 'personal-s7-hanging-leg-raise', name: '悬垂举腿', bodyPart: 'abs', equipment: 'bodyweight', subCategory: 'lower-abs', tips: ['核心发力带动腿部抬起', '不借力摆动'], sets: 3, reps: '12', rpe: 8, restSeconds: 60, isBodyweight: true, source: '私教版', demoUrl: '/media/gifs/personal-s7-hanging-leg-raise', videoUrl: '/media/videos/personal-s7-hanging-leg-raise' },
  { id: 'personal-s7-crunch', name: '仰卧卷腹', bodyPart: 'abs', equipment: 'bodyweight', subCategory: 'upper-abs', tips: ['肩胛骨离地即可', '感受上腹收缩'], sets: 3, reps: '15', rpe: 7, restSeconds: 45, isBodyweight: true, source: '私教版', demoUrl: '/media/gifs/personal-s7-crunch', videoUrl: '/media/videos/personal-s7-crunch' },
  { id: 'personal-s7-ab-wheel', name: '健腹轮', bodyPart: 'abs', equipment: 'ab-wheel', subCategory: 'core', tips: ['核心全程收紧', '不塌腰不弓背'], sets: 3, reps: '10', rpe: 8, restSeconds: 60, isBodyweight: false, source: '私教版', demoUrl: '/media/gifs/personal-s7-ab-wheel', videoUrl: '/media/videos/personal-s7-ab-wheel' },

  // --- 第8期 手臂训练·二（3个超级组·全部正式） ---
  { id: 'personal-s8-cable-tricep-pushdown', name: '绳索臂屈伸', bodyPart: 'arms', equipment: 'cable', subCategory: 'triceps', tips: ['超级组第1个动作·三头', '大臂固定不晃动', '与绳索弯举交替无休息'], sets: 3, reps: '12', rpe: 8, restSeconds: 90, isBodyweight: false, source: '私教超级组', demoUrl: '/media/gifs/personal-s8-cable-tricep-pushdown', videoUrl: '/media/videos/personal-s8-cable-tricep-pushdown' },
  { id: 'personal-s8-barbell-supine-standing', name: '仰卧杠铃臂伸站姿弯举', bodyPart: 'arms', equipment: 'barbell', subCategory: 'arms', tips: ['超级组·三头+二头组合', '仰卧杠铃臂屈伸接站姿弯举', '组间休息90秒'], sets: 3, reps: '10+10', rpe: 8, restSeconds: 90, isBodyweight: false, source: '私教超级组', demoUrl: '/media/gifs/personal-s8-barbell-supine-standing', videoUrl: '/media/videos/personal-s8-barbell-supine-standing' },
];

PERSONAL_EXERCISES.forEach(e => MOCK_EXERCISES.push(e));

// ========== 晋升突破 B合集（16期，fanjue- 前缀） ==========
const FANJUE_EXERCISES: IExercise[] = [
  // 第01期
  {
    id: 'fanjue-scapular-activation',
    name: '凳上肩胛骨激活',
    bodyPart: 'back', equipment: 'bodyweight', subCategory: 'thoracic',
    tips: ['肩胛前引后缩', '控制动作节奏'],
    sets: 2, reps: '15次', rpe: 5, restSeconds: 30, isBodyweight: true, isWarmup: true,
    demoUrl: '/media/gifs/fanjue-scapular-activation',
    videoUrl: '/media/videos/fanjue-scapular-activation',
  },
  {
    id: 'fanjue-standing-chest-shoulder-stretch',
    name: '站立肩胸拉伸',
    bodyPart: 'chest', equipment: 'bodyweight', subCategory: 'chest',
    tips: ['感受胸肩前侧拉伸', '每侧30秒'],
    sets: 2, reps: '15次', rpe: 4, restSeconds: 30, isBodyweight: true, isWarmup: true,
    demoUrl: '/media/gifs/fanjue-standing-chest-shoulder-stretch',
    videoUrl: '/media/videos/fanjue-standing-chest-shoulder-stretch',
  },
  {
    id: 'fanjue-wide-pulldown',
    name: '宽握高位下拉',
    bodyPart: 'back', equipment: 'cable', subCategory: 'lat',
    tips: ['宽握距更大程度拉伸背阔', '拉至锁骨位置'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 75, isBodyweight: false,
    demoUrl: '/media/gifs/fanjue-wide-pulldown',
    videoUrl: '/media/videos/fanjue-wide-pulldown',
  },
  {
    id: 'fanjue-straight-arm-pulldown',
    name: '绳索直臂下压',
    bodyPart: 'arms', equipment: 'cable', subCategory: 'triceps',
    tips: ['手臂保持伸直', '感受背阔肌长头拉伸'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: false,
    demoUrl: '/media/gifs/fanjue-straight-arm-pulldown',
    videoUrl: '/media/videos/fanjue-straight-arm-pulldown',
  },
  // 第02期
  {
    id: 'fanjue-prone-machine-row',
    name: '俯身器械划船',
    bodyPart: 'back', equipment: 'machine', subCategory: 'row',
    tips: ['胸贴凳面', '拉至下腹部'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 75, isBodyweight: false,
    demoUrl: '/media/gifs/fanjue-prone-machine-row',
    videoUrl: '/media/videos/fanjue-prone-machine-row',
  },
  {
    id: 'fanjue-supinated-straight-arm-pulldown',
    name: '反握直臂下压',
    bodyPart: 'arms', equipment: 'cable', subCategory: 'triceps',
    tips: ['反握增加二头参与', '全程控制'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: false,
    demoUrl: '/media/gifs/fanjue-supinated-straight-arm-pulldown',
    videoUrl: '/media/videos/fanjue-supinated-straight-arm-pulldown',
  },
  {
    id: 'fanjue-seated-machine-row',
    name: '坐姿器械划船',
    bodyPart: 'back', equipment: 'machine', subCategory: 'row',
    tips: ['背部挺直', '肩胛后缩挤压'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 75, isBodyweight: false,
    demoUrl: '/media/gifs/fanjue-seated-machine-row',
    videoUrl: '/media/videos/fanjue-seated-machine-row',
  },
  {
    id: 'fanjue-lat-pulldown',
    name: '高位下拉',
    bodyPart: 'back', equipment: 'cable', subCategory: 'lat',
    tips: ['沉肩下拉', '感受背阔收缩'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 75, isBodyweight: false,
    demoUrl: '/media/gifs/fanjue-lat-pulldown',
    videoUrl: '/media/videos/fanjue-lat-pulldown',
  },
  // 第03期（核心，仅讲解视频无动作演示）
  // 第04期
  {
    id: 'fanjue-elbow-bench-stretch',
    name: '手肘搭平板凳',
    bodyPart: 'shoulders', equipment: 'bodyweight', subCategory: 'thoracic',
    tips: ['胸椎旋转活动', '每侧保持30秒'],
    sets: 2, reps: '15次', rpe: 4, restSeconds: 30, isBodyweight: true, isWarmup: true,
    demoUrl: '/media/gifs/fanjue-elbow-bench-stretch',
    videoUrl: '/media/videos/fanjue-elbow-bench-stretch',
  },
  {
    id: 'fanjue-spider-warmup',
    name: '蜘蛛式热身',
    bodyPart: 'warmup', equipment: 'bodyweight', subCategory: 'hip-leg',
    tips: ['髋部下沉', '动态拉伸髋屈肌'],
    sets: 2, reps: '15次', rpe: 5, restSeconds: 30, isBodyweight: true, isWarmup: true,
    demoUrl: '/media/gifs/fanjue-spider-warmup',
    videoUrl: '/media/videos/fanjue-spider-warmup',
  },
  {
    id: 'fanjue-seated-cable-row',
    name: '坐姿绳索划船',
    bodyPart: 'back', equipment: 'cable', subCategory: 'row',
    tips: ['躯干保持稳定', '拉至小腹位置'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 75, isBodyweight: false,
    demoUrl: '/media/gifs/fanjue-seated-cable-row',
    videoUrl: '/media/videos/fanjue-seated-cable-row',
  },
  // 第05期（腿）
  {
    id: 'fanjue-bulgarian-squat',
    name: '保加利亚蹲',
    bodyPart: 'legs', equipment: 'bodyweight', subCategory: 'quads-glutes',
    tips: ['前腿膝盖不超脚尖', '重心在前脚'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: true,
    demoUrl: '/media/gifs/fanjue-bulgarian-squat',
    videoUrl: '/media/videos/fanjue-bulgarian-squat',
  },
  // 第06期（腿）
  // 第07期（肩后束）
  {
    id: 'fanjue-external-rotation-press',
    name: '外旋位推肩',
    bodyPart: 'shoulders', equipment: 'bodyweight', subCategory: 'shoulder',
    tips: ['外旋位启动', '激活肩袖肌群'],
    sets: 2, reps: '15次', rpe: 5, restSeconds: 30, isBodyweight: true, isWarmup: true,
    demoUrl: '/media/gifs/fanjue-external-rotation-press',
    videoUrl: '/media/videos/fanjue-external-rotation-press',
  },
  {
    id: 'fanjue-head-ground-press',
    name: '头枕地对抗',
    bodyPart: 'shoulders', equipment: 'bodyweight', subCategory: 'thoracic',
    tips: ['仰卧头轻压地面', '深层颈屈肌激活'],
    sets: 2, reps: '15次', rpe: 4, restSeconds: 30, isBodyweight: true, isWarmup: true,
    demoUrl: '/media/gifs/fanjue-head-ground-press',
    videoUrl: '/media/videos/fanjue-head-ground-press',
  },
  {
    id: 'fanjue-stretch-counter',
    name: '拉伸对抗',
    bodyPart: 'stretch', equipment: 'bodyweight', subCategory: 'shoulder',
    tips: ['拉伸与对抗交替', '增加关节活动度'],
    sets: 2, reps: '15次', rpe: 4, restSeconds: 30, isBodyweight: true, isWarmup: true,
    demoUrl: '/media/gifs/fanjue-stretch-counter',
    videoUrl: '/media/videos/fanjue-stretch-counter',
  },
  {
    id: 'fanjue-foam-roller-support',
    name: '泡沫轴支撑',
    bodyPart: 'shoulders', equipment: 'foam-roller', subCategory: 'thoracic',
    tips: ['胸椎段泡沫轴放松', '上下滚动'],
    sets: 2, reps: '15次', rpe: 4, restSeconds: 30, isBodyweight: false, isWarmup: true,
    demoUrl: '/media/gifs/fanjue-foam-roller-support',
    videoUrl: '/media/videos/fanjue-foam-roller-support',
  },
  {
    id: 'fanjue-rear-delt-bodyweight',
    name: '三角肌后束',
    bodyPart: 'shoulders', equipment: 'bodyweight', subCategory: 'rear-deltoid',
    tips: ['俯卧位后束收缩', '顶峰保持1秒'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 45, isBodyweight: true,
    demoUrl: '/media/gifs/fanjue-rear-delt-bodyweight',
    videoUrl: '/media/videos/fanjue-rear-delt-bodyweight',
  },
  // 第08期（背）
  {
    id: 'fanjue-internal-rotation-activation',
    name: '内旋肌激活',
    bodyPart: 'shoulders', equipment: 'bodyweight', subCategory: 'shoulder',
    tips: ['肩内旋肌群激活', '改善圆肩体态'],
    sets: 2, reps: '15次', rpe: 5, restSeconds: 30, isBodyweight: true, isWarmup: true,
    demoUrl: '/media/gifs/fanjue-internal-rotation-activation',
    videoUrl: '/media/videos/fanjue-internal-rotation-activation',
  },
  {
    id: 'fanjue-elbow-extension-tricep',
    name: '肘屈伸',
    bodyPart: 'arms', equipment: 'bodyweight', subCategory: 'triceps',
    tips: ['大臂固定', '肱三头肌充分收缩'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 45, isBodyweight: true,
    demoUrl: '/media/gifs/fanjue-elbow-extension-tricep',
    videoUrl: '/media/videos/fanjue-elbow-extension-tricep',
  },
  // 第09期（胸）
  {
    id: 'fanjue-kneeling-pushup',
    name: '跪姿俯卧撑',
    bodyPart: 'arms', equipment: 'bodyweight', subCategory: 'chest',
    tips: ['跪姿降低难度', '胸部主导发力'],
    sets: 2, reps: '15次', rpe: 5, restSeconds: 30, isBodyweight: true, isWarmup: true,
    demoUrl: '/media/gifs/fanjue-kneeling-pushup',
    videoUrl: '/media/videos/fanjue-kneeling-pushup',
  },
  {
    id: 'fanjue-incline-barbell-press',
    name: '上斜卧推',
    bodyPart: 'chest', equipment: 'barbell', subCategory: 'upper-chest',
    tips: ['上斜30度', '杠铃下放至胸上沿'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 75, isBodyweight: false,
    demoUrl: '/media/gifs/fanjue-incline-barbell-press',
    videoUrl: '/media/videos/fanjue-incline-barbell-press',
  },
  // 第10期（肩）
  {
    id: 'fanjue-supine-face-pull',
    name: '仰卧面拉',
    bodyPart: 'shoulders', equipment: 'cable', subCategory: 'shoulder',
    tips: ['仰卧位面拉', '后束与肩袖激活'],
    sets: 2, reps: '15次', rpe: 5, restSeconds: 30, isBodyweight: false, isWarmup: true,
    demoUrl: '/media/gifs/fanjue-supine-face-pull',
    videoUrl: '/media/videos/fanjue-supine-face-pull',
  },
  {
    id: 'fanjue-rotator-cuff-activation',
    name: '肩袖激活',
    bodyPart: 'warmup', equipment: 'resistance-band', subCategory: 'shoulder',
    tips: ['弹力带外旋内旋', '肩袖肌群预热'],
    sets: 2, reps: '15次', rpe: 5, restSeconds: 30, isBodyweight: false, isWarmup: true,
    demoUrl: '/media/gifs/fanjue-rotator-cuff-activation',
    videoUrl: '/media/videos/fanjue-rotator-cuff-activation',
  },
  {
    id: 'fanjue-standing-military-press',
    name: '实力推',
    bodyPart: 'shoulders', equipment: 'barbell', subCategory: 'front-deltoid',
    tips: ['核心收紧', '推举至头顶'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 75, isBodyweight: false,
    demoUrl: '/media/gifs/fanjue-standing-military-press',
    videoUrl: '/media/videos/fanjue-standing-military-press',
  },
  // 第11期（胸）
  {
    id: 'fanjue-chest-warmup',
    name: '胸部热身',
    bodyPart: 'chest', equipment: 'bodyweight', subCategory: 'chest',
    tips: ['胸部肌群动态激活', '俯卧撑变体'],
    sets: 2, reps: '15次', rpe: 5, restSeconds: 30, isBodyweight: true, isWarmup: true,
    demoUrl: '/media/gifs/fanjue-chest-warmup',
    videoUrl: '/media/videos/fanjue-chest-warmup',
  },
  {
    id: 'fanjue-incline-smith-press',
    name: '上斜史密斯',
    bodyPart: 'chest', equipment: 'machine', subCategory: 'upper-chest',
    tips: ['史密斯轨迹稳定', '侧重上胸'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 75, isBodyweight: false,
    demoUrl: '/media/gifs/fanjue-incline-smith-press',
    videoUrl: '/media/videos/fanjue-incline-smith-press',
  },
  {
    id: 'fanjue-incline-machine-chest-press',
    name: '上斜推胸',
    bodyPart: 'chest', equipment: 'machine', subCategory: 'upper-chest',
    tips: ['器械上斜推胸', '顶峰收缩'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 75, isBodyweight: false,
    demoUrl: '/media/gifs/fanjue-incline-machine-chest-press',
    videoUrl: '/media/videos/fanjue-incline-machine-chest-press',
  },
  // 第12期（肩）
  {
    id: 'fanjue-dumbbell-front-raise',
    name: '哑铃前平举',
    bodyPart: 'shoulders', equipment: 'dumbbell', subCategory: 'front-deltoid',
    tips: ['前平举刺激前束', '交替或双手'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 45, isBodyweight: false,
    demoUrl: '/media/gifs/fanjue-dumbbell-front-raise',
    videoUrl: '/media/videos/fanjue-dumbbell-front-raise',
  },
  // 第13期（核心）
  {
    id: 'fanjue-reverse-hyperextension-crunch',
    name: '反向山羊挺身卷腹',
    bodyPart: 'abs', equipment: 'bodyweight', subCategory: 'lower-abs',
    tips: ['下背部+下腹复合', '控制幅度'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 45, isBodyweight: true,
    demoUrl: '/media/gifs/fanjue-reverse-hyperextension-crunch',
    videoUrl: '/media/videos/fanjue-reverse-hyperextension-crunch',
  },
  {
    id: 'fanjue-cable-crunch-kneeling',
    name: '跪姿龙门架磕头',
    bodyPart: 'abs', equipment: 'cable', subCategory: 'upper-abs',
    tips: ['绳索卷腹', '上腹顶峰收缩'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 45, isBodyweight: false,
    demoUrl: '/media/gifs/fanjue-cable-crunch-kneeling',
    videoUrl: '/media/videos/fanjue-cable-crunch-kneeling',
  },
  // 第14期（手臂）
  {
    id: 'fanjue-bent-barbell-curl',
    name: '俯身杠铃弯举',
    bodyPart: 'arms', equipment: 'barbell', subCategory: 'biceps',
    tips: ['俯身位弯举', '二头峰值收缩'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 45, isBodyweight: false,
    demoUrl: '/media/gifs/fanjue-bent-barbell-curl',
    videoUrl: '/media/videos/fanjue-bent-barbell-curl',
  },
  {
    id: 'fanjue-single-arm-cable-tricep',
    name: '单臂绳索臂屈伸',
    bodyPart: 'arms', equipment: 'cable', subCategory: 'triceps',
    tips: ['单臂绳索下压', '侧重三头外侧头'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 45, isBodyweight: false,
    demoUrl: '/media/gifs/fanjue-single-arm-cable-tricep',
    videoUrl: '/media/videos/fanjue-single-arm-cable-tricep',
  },
  {
    id: 'fanjue-straight-bar-tricep',
    name: '直杆臂屈伸',
    bodyPart: 'arms', equipment: 'cable', subCategory: 'triceps',
    tips: ['直杆绳索下压', '三头整体刺激'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 45, isBodyweight: false,
    demoUrl: '/media/gifs/fanjue-straight-bar-tricep',
    videoUrl: '/media/videos/fanjue-straight-bar-tricep',
  },
  {
    id: 'fanjue-cable-hammer-curl',
    name: '绳索锤式弯举',
    bodyPart: 'arms', equipment: 'cable', subCategory: 'biceps',
    tips: ['锤式弯举', '肱肌+肱桡肌'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 45, isBodyweight: false,
    demoUrl: '/media/gifs/fanjue-cable-hammer-curl',
    videoUrl: '/media/videos/fanjue-cable-hammer-curl',
  },
  // 第15期（臀）
  {
    id: 'fanjue-side-band-walk',
    name: '侧向移动弹力带',
    bodyPart: 'warmup', equipment: 'resistance-band', subCategory: 'hip-leg',
    tips: ['侧步走激活臀中肌', '弹力带始终张力'],
    sets: 2, reps: '15次', rpe: 5, restSeconds: 30, isBodyweight: false, isWarmup: true,
    demoUrl: '/media/gifs/fanjue-side-band-walk',
    videoUrl: '/media/videos/fanjue-side-band-walk',
  },
  {
    id: 'fanjue-gluteus-minimus-activation',
    name: '臀中肌臀小肌激活',
    bodyPart: 'legs', equipment: 'bodyweight', subCategory: 'hip-leg',
    tips: ['蚌式等臀部激活', '感受臀中肌收缩'],
    sets: 2, reps: '15次', rpe: 5, restSeconds: 30, isBodyweight: true, isWarmup: true,
    demoUrl: '/media/gifs/fanjue-gluteus-minimus-activation',
    videoUrl: '/media/videos/fanjue-gluteus-minimus-activation',
  },
  {
    id: 'fanjue-bulgarian-glute',
    name: '保加利亚蹲',
    bodyPart: 'legs', equipment: 'bodyweight', subCategory: 'quads-glutes',
    tips: ['侧重臀部版本', '重心后移'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: true,
    demoUrl: '/media/gifs/fanjue-bulgarian-glute',
    videoUrl: '/media/videos/fanjue-bulgarian-glute',
  },
  {
    id: 'fanjue-copenhagen-plank',
    name: '哥本哈根支撑',
    bodyPart: 'abs', equipment: 'bodyweight', subCategory: 'obliques',
    tips: ['侧支撑变体', '核心+内收肌'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 45, isBodyweight: true,
    demoUrl: '/media/gifs/fanjue-copenhagen-plank',
    videoUrl: '/media/videos/fanjue-copenhagen-plank',
  },
  {
    id: 'fanjue-wide-body-function',
    name: '宽身功能训练',
    bodyPart: 'legs', equipment: 'bodyweight', subCategory: 'hamstrings',
    tips: ['宽距下肢功能训练', '后侧链为主'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: true,
    demoUrl: '/media/gifs/fanjue-wide-body-function',
    videoUrl: '/media/videos/fanjue-wide-body-function',
  },
  {
    id: 'fanjue-cable-twist',
    name: '龙门架旋转',
    bodyPart: 'abs', equipment: 'cable', subCategory: 'obliques',
    tips: ['绳索伐木旋转', '侧重腹斜肌'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 45, isBodyweight: false,
    demoUrl: '/media/gifs/fanjue-cable-twist',
    videoUrl: '/media/videos/fanjue-cable-twist',
  },
  // 第16期（腿）
  {
    id: 'fanjue-leg-warmup',
    name: '腿部热身',
    bodyPart: 'legs', equipment: 'bodyweight', subCategory: 'hip-leg',
    tips: ['下肢动态热身', '关节活动+肌肉激活'],
    sets: 2, reps: '15次', rpe: 5, restSeconds: 30, isBodyweight: true, isWarmup: true,
    demoUrl: '/media/gifs/fanjue-leg-warmup',
    videoUrl: '/media/videos/fanjue-leg-warmup',
  },
  {
    id: 'fanjue-bulgarian-leg',
    name: '保加利亚蹲',
    bodyPart: 'legs', equipment: 'bodyweight', subCategory: 'quads-glutes',
    tips: ['腿部主导版本', '股四头侧重'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: true,
    demoUrl: '/media/gifs/fanjue-bulgarian-leg',
    videoUrl: '/media/videos/fanjue-bulgarian-leg',
  },
  {
    id: 'fanjue-seated-leg-extension',
    name: '坐姿腿屈伸',
    bodyPart: 'legs', equipment: 'machine', subCategory: 'quadriceps',
    tips: ['坐姿腿屈伸', '股四头孤立'],
    sets: 3, reps: '12-15', rpe: 8, restSeconds: 60, isBodyweight: false,
    demoUrl: '/media/gifs/fanjue-seated-leg-extension',
    videoUrl: '/media/videos/fanjue-seated-leg-extension',
  },
];

// 将晋升突破动作并入总库
FANJUE_EXERCISES.forEach(e => MOCK_EXERCISES.push(e));

// ========== 动作标签自动补齐 ==========
// 基于动作名称、bodyPart、equipment、subCategory 等推断合理的 targetMuscles 和 difficulty
// 规则：按名称关键词匹配 → 按 bodyPart+subCategory 兜底 → 通用默认值
function inferTargetMuscles(ex: IExercise): string[] {
  if (ex.targetMuscles && ex.targetMuscles.length > 0) return ex.targetMuscles;
  if (ex.isWarmup) return ['全身激活肌群'];
  if (ex.isStretch) return ['全身拉伸肌群'];

  const name = ex.name;
  const part = ex.bodyPart;
  const sub = ex.subCategory;
  const muscles: string[] = [];

  // ===== 胸部 =====
  if (part === 'chest') {
    if (name.includes('上斜') || sub === 'upper-chest') muscles.push('上胸肌');
    if (name.includes('下斜') || sub === 'lower-chest' || name.includes('下胸')) muscles.push('下胸肌');
    if (name.includes('夹胸') || name.includes('飞鸟') || sub === 'inner-chest' || name.includes('中缝')) muscles.push('胸大肌中缝');
    if (name.includes('卧推') || name.includes('推胸') || sub === 'mid-chest') muscles.push('胸大肌');
    if (name.includes('俯卧撑') || name.includes('臂屈伸')) muscles.push('胸大肌');
    if (name.includes('前锯肌')) muscles.push('前锯肌');
    if (muscles.length === 0) muscles.push('胸大肌');
    // 推类动作共同协同肌
    if (name.includes('推') || name.includes('卧推') || name.includes('俯卧撑')) muscles.push('三角肌前束', '肱三头肌');
    return Array.from(new Set(muscles));
  }

  // ===== 背部 =====
  if (part === 'back') {
    if (name.includes('下拉') || sub === 'lat' || name.includes('引体')) muscles.push('背阔肌');
    if (name.includes('划船') || sub === 'row' || name.includes('T杆')) muscles.push('背阔肌', '菱形肌', '斜方肌中下部');
    if (name.includes('硬拉')) muscles.push('竖脊肌', '臀大肌', '腘绳肌');
    if (name.includes('耸肩') || sub === 'traps') muscles.push('斜方肌上部');
    if (name.includes('面拉') || name.includes('反向飞鸟')) muscles.push('后三角肌', '斜方肌中下部', '菱形肌');
    if (name.includes('直臂下压')) muscles.push('背阔肌', '大圆肌');
    if (muscles.length === 0) muscles.push('背阔肌');
    if (name.includes('拉') || name.includes('划船') || name.includes('下拉')) muscles.push('肱二头肌');
    return Array.from(new Set(muscles));
  }

  // ===== 肩部 =====
  if (part === 'shoulders') {
    if (name.includes('前束') || sub === 'front-deltoid' || name.includes('实力推') || name.includes('推举')) muscles.push('三角肌前束');
    if (name.includes('侧平举') || name.includes('侧束') || sub === 'side-deltoid') muscles.push('三角肌中束');
    if (name.includes('后束') || name.includes('面拉') || sub === 'rear-deltoid') muscles.push('三角肌后束');
    if (name.includes('推') || name.includes('推举') || name.includes('肩推')) muscles.push('三角肌前束', '三角肌中束');
    if (muscles.length === 0) muscles.push('三角肌');
    if (name.includes('推') || name.includes('推举')) muscles.push('肱三头肌');
    return Array.from(new Set(muscles));
  }

  // ===== 腿部 =====
  if (part === 'legs') {
    if (name.includes('深蹲') || name.includes('腿举') || name.includes('腿屈伸') || sub === 'quads' || name.includes('蹲')) muscles.push('股四头肌');
    if (name.includes('硬拉') || name.includes('腿弯举') || name.includes('腘绳') || sub === 'hamstrings') muscles.push('腘绳肌');
    if (name.includes('臀') || name.includes('髋外展') || sub === 'glutes') muscles.push('臀大肌');
    if (name.includes('箭步') || name.includes('弓步') || name.includes('箭步蹲')) muscles.push('股四头肌', '臀大肌');
    if (muscles.length === 0) muscles.push('股四头肌');
    if (name.includes('深蹲') || name.includes('硬拉') || name.includes('腿举')) muscles.push('臀大肌');
    return Array.from(new Set(muscles));
  }

  // ===== 手臂 =====
  if (part === 'arms') {
    if (sub === 'biceps' || name.includes('弯举') || name.includes('二头')) muscles.push('肱二头肌');
    if (sub === 'triceps' || name.includes('臂屈伸') || name.includes('三头') || name.includes('下压')) muscles.push('肱三头肌');
    if (name.includes('前臂') || name.includes('腕弯')) muscles.push('前臂肌群');
    if (muscles.length === 0) muscles.push('手臂肌群');
    return Array.from(new Set(muscles));
  }

  // ===== 腹部/核心 =====
  if (part === 'abs') {
    if (name.includes('卷腹') || name.includes('仰卧起坐') || sub === 'upper-abs') muscles.push('腹直肌上部');
    if (name.includes('抬腿') || name.includes('举腿') || sub === 'lower-abs') muscles.push('腹直肌下部');
    if (name.includes('侧') || name.includes('转体') || sub === 'obliques') muscles.push('腹斜肌');
    if (name.includes('平板') || name.includes('支撑') || sub === 'core') muscles.push('核心肌群');
    if (muscles.length === 0) muscles.push('腹直肌');
    return Array.from(new Set(muscles));
  }

  // ===== 小腿 =====
  if (part === 'calves') {
    muscles.push('腓肠肌', '比目鱼肌');
    return muscles;
  }

  // 兜底
  const partLabels: Record<string, string> = { chest: '胸部', back: '背部', shoulders: '肩部', legs: '腿部', arms: '手臂', abs: '腹部', warmup: '热身', stretch: '拉伸', calves: '小腿' };
  return [partLabels[part] || '目标肌群'];
}

function inferDifficulty(ex: IExercise): DifficultyLevel {
  if (ex.difficulty) return ex.difficulty;

  const name = ex.name;
  const part = ex.bodyPart;
  const eq = ex.equipment;

  // 热身/拉伸都是入门
  if (ex.isWarmup || ex.isStretch) return 'beginner';

  // 徒手入门级
  if (ex.isBodyweight && (name.includes('跪姿') || name.includes('简易') || name.includes('入门'))) return 'beginner';

  // 复合动作（大重量基础）= 进阶
  if (
    name.includes('杠铃') && (
      name.includes('卧推') || name.includes('深蹲') || name.includes('硬拉') ||
      name.includes('推举') || name.includes('划船') || name.includes('实力推')
    )
  ) return 'advanced';

  // 自由重量复合动作 = 进阶
  if (name.includes('硬拉') || name.includes('深蹲') || name.includes('抓举') || name.includes('挺举')) return 'advanced';

  // 固定器械 = 入门（对新手友好）
  if (eq === 'machine') return 'beginner';

  // 徒手基础动作
  if (ex.isBodyweight && (name.includes('俯卧撑') || name.includes('深蹲') || name.includes('卷腹'))) return 'beginner';

  // 孤立动作（哑铃/绳索单关节）= 中级
  if (eq === 'dumbbell' && (name.includes('飞鸟') || name.includes('侧平举') || name.includes('弯举') || name.includes('臂屈伸'))) return 'intermediate';
  if (eq === 'cable' && (name.includes('夹胸') || name.includes('下压') || name.includes('面拉'))) return 'intermediate';

  // 热身/拉伸部位 = 入门
  if (part === 'warmup' || part === 'stretch') return 'beginner';

  // 腹部/小腿大多数动作 = 入门~中级
  if (part === 'abs' || part === 'calves') return 'beginner';

  // 兜底：自由重量 = 中级，器械 = 入门
  if (eq === 'barbell' || eq === 'dumbbell') return 'intermediate';
  return 'beginner';
}

// 对全部动作执行标签补齐（仅在数据层补齐，不修改原始 MOCK_EXERCISES 引用）
let _enrichedExercises: IExercise[] | null = null;
function getAllExercisesEnriched(): IExercise[] {
  if (_enrichedExercises) return _enrichedExercises;
  _enrichedExercises = MOCK_EXERCISES.map(ex => ({
    ...ex,
    targetMuscles: inferTargetMuscles(ex),
    difficulty: inferDifficulty(ex),
  }));
  return _enrichedExercises;
}
